import { customerContactDocument } from '../../utils/mailMergeFields';
import { IContextualMenuItem } from '@fluentui/react';
import {
  AccountBalanceCriteria,
  AccountingScheme,
  AccountType,
  AccrualPrepaymentType,
  ActionAllowed,
  AdditionalInstructionSource,
  AlertPosition,
  AlertType,
  AllocationMethod,
  AlternativeProductType,
  ArchivedType,
  AssemblyStatus,
  AttributeType,
  AutomatedCreditControlActionType,
  AutomatedCreditControlTargetContact,
  AvailabilityCalculationBasis,
  BankChargesAccount,
  BankPaymentReceiptType,
  BinLocationSelection,
  BinReplenishmentStatus,
  BusinessRuleName,
  CapacityField,
  CarriageCalculationMethod,
  CarriageCostCalculationMethod,
  CarrierIntegrationType,
  CashReceivedType,
  CashStatus,
  ChannelFeatureStatus,
  ChargeBasis,
  CompanyUserTheme,
  ComparisonOperation,
  CostOfSaleMethod,
  CostPrice,
  CostViewPermission,
  CustomerAlertForm,
  CustomerContactDeliveryType,
  CustomerPriceListVatInclusive,
  CustomerSpecialPriceOrigin,
  CustomerStrategy,
  DashboardWidgetCalculation,
  DatabaseDriver,
  DayOfWeek,
  DeliveryNoteRule,
  DeliveryRouteTimeRestriction,
  DirectDebitFrequency,
  DirectDebitRequestOutputFormat,
  Direction,
  DiscountApplicationMethod,
  DiscountType,
  DispatchRule,
  DocumentStatus,
  DocumentSummaryStatus,
  DocumentType,
  EdiType,
  EmailType,
  Entity,
  EuCountry,
  FeedRuleAmountCondition,
  FeedRuleStringCondition,
  FilterDateRelative,
  FilterDateType,
  FilterNumberType,
  FilterStringType,
  FilterStyle,
  FinancialCalendarType,
  FinancialPeriodStatus,
  Frequency,
  GenericChannelType,
  GoodsReceiptOrigin,
  HeldAction,
  ImportRecordType,
  IncludeAllLines,
  InvoiceConsolidationOption,
  InvoiceFrequency,
  Involves,
  IssueReceipt,
  JournalType,
  JournalVatType,
  LabelFormat,
  LabelPrintOption,
  LengthUnitOfMeasure,
  MarginRecalculationMethod,
  Marketplace,
  MassUom,
  MaxoptraOption,
  MaxoptraOrderPriority,
  MaxoptraOrderStatus,
  MinMaxCalculationMethod,
  MonthlyOccurrence,
  NivoGraphPattern,
  NominalCompareOption,
  NotificationType,
  NumericConfig,
  Operation,
  OrderEventLeadTime,
  OrderImportFailureReason,
  OrderOrigin,
  OrderType,
  PayByLinkPaymentStatus,
  PaymentInterval,
  PaymentReceiptAutoAllocateSequence,
  PaymentStatus,
  PaymentTermDate,
  PaymentType,
  PickingGroupSelection,
  PickingPrintOrder,
  PickingSelection,
  PickType,
  PostingPermissionType,
  Price,
  PriceMatrixOrigin,
  PriceSelectionPermission,
  PriceUpdate,
  PrinterType,
  ProductAlertForm,
  ProductClass,
  ProductOrigin,
  ProductSpecialContractPriceOnly,
  PurchaseEdiType,
  PurchaseOrderLineStatus,
  PurchaseOrderMatchingType,
  PurchasePaymentExportType,
  PurchasePaymentStatus,
  QuantityToPick,
  QuoteEventLeadTime,
  QuoteStatus,
  ReceiptLabelType,
  RecurrenceType,
  ReportLayoutAlignment,
  ReportLayoutCredit,
  ReportLayoutLineType,
  ReportLayoutStyle,
  ReportOutputType,
  ReportTemplateFormat,
  ReportType,
  RestockOption,
  SalesCreditType,
  SalesMarginType,
  SalesOrderActivationType,
  SalesOrderLineStatus,
  SalesOrderPromptBehaviour,
  SalesValueComparisonType,
  ScanFormStatus,
  SearchBehaviour,
  ShipTheoryReferenceOption,
  ShortageOrigin,
  Site,
  SmtpAuthMechanism,
  StatementTransactions,
  StockAdjustmentForInsufficientStock,
  StockCommitmentType,
  StockLotSortOption,
  StockTakeMovementType,
  StockTakeType,
  StockTransactionReason,
  SupplierAlertForm,
  SupplierPaymentPriority,
  SupplierPaymentType,
  SupplierReturnStatus,
  TargetDateSource,
  TemplateType,
  TermsOfDelivery,
  Title,
  TransactionNoteType,
  TransactionNotification,
  TransactionResult,
  TransactionType,
  UnitOfMeasureFactorDirection,
  UserActionScope,
  ValuationMethod,
  VarianceApproval,
  VatObligationStatus,
  VatOnlyTransactionType,
  VatType,
  WarehouseType,
  WindsorFrameworkAtRiskStatus,
  YesNoPrompt,
  RagStatus,
  WeightType,
  PackagingUse,
  WasteType,
  EprPeriod,
} from './p4types';
import { WidgetWorkspace } from '../../@types/enums';
import { enumToTitleCase } from '../../utils/TitleCase';

export type IWorkspaceExclusionMetaData = {
  excludeFromWorkspace: boolean;
};

export type IColumnMetaData = {
  [key: string]: {
    hint?: string;
    title?: string;
  };
};

export type IEnumMeta<EnumType> = {
  value: EnumType | null;
  label: string;
  mergeFields?: Array<IContextualMenuItem>;
  recordClass?: string;
  filter?: {
    //May be incomplete.
    [key: string]: boolean;
  };
  leveled?: boolean;
};

export type IEnumMetaData<EnumType = string> = {
  enumValues: Array<IEnumMeta<EnumType>>;
  enumValuesLimited?: Array<IEnumMeta<EnumType>>;
};

export type IFieldMetaData = {
  [key: string]: IEnumMetaData | IColumnMetaData | IWorkspaceExclusionMetaData;
  AttributeType: IEnumMetaData;
  DocumentType: IEnumMetaData;
  SupplierReturnStatus: IEnumMetaData;
  ValuationMethod: IEnumMetaData<ValuationMethod>;
  Title: IEnumMetaData;
  BankPaymentReceiptType: IEnumMetaData;
  ReportType: IEnumMetaData;
  ReportTemplateFormat: IEnumMetaData;
  OrderImportFailureReason: IEnumMetaData;
  SalesDocumentType: IEnumMetaData;
  PrinterType: IEnumMetaData;
  LabelFormat: IEnumMetaData;
  AccountType: IEnumMetaData<AccountType>;
  PurchaseDocumentType: IEnumMetaData;
  PurchaseOrderLineStatus: IEnumMetaData<PurchaseOrderLineStatus>;
  ChargeBasis: IEnumMetaData<ChargeBasis>;
  Entity: IEnumMetaData<Entity>;
  AccrualPrepaymentType: IEnumMetaData<AccrualPrepaymentType>;
  JournalType: IEnumMetaData<JournalType>;
  DocumentStatus: IEnumMetaData<DocumentStatus>;
  JournalVatType: IEnumMetaData<JournalVatType>;
  ComparisonOperation: IEnumMetaData<ComparisonOperation>;
  CashReceivedType: IEnumMetaData<CashReceivedType>;
  CashStatus: IEnumMetaData<CashStatus>;
  AllocationMethod: IEnumMetaData<AllocationMethod>;
  BinaryActionAllowed: IEnumMetaData<ActionAllowed>;
  ActionAllowed: IEnumMetaData<ActionAllowed>;
  ActionAllowedWarning: IEnumMetaData<ActionAllowed>;
  BusinessRuleName: IEnumMetaData<BusinessRuleName>;
  PaymentType: IEnumMetaData<PaymentType>;
  VatOnlyTransactionType: IEnumMetaData<VatOnlyTransactionType>;
  VatType: IEnumMetaData<VatType>;
  SalesOrderLineStatus: IEnumMetaData<SalesOrderLineStatus>;
  AssemblyStatus: IEnumMetaData<AssemblyStatus>;
  DocumentOutputType: IEnumMetaData<ReportOutputType>;
  ReportOutputType: IEnumMetaData<ReportOutputType>;
  StockTransactionReason: IEnumMetaData<StockTransactionReason>;
  PickingSelection: IEnumMetaData<PickingSelection>;
  PickingGroupSelection: IEnumMetaData<PickingGroupSelection>;
  HeldAction: IEnumMetaData<HeldAction>;
  PickingPrintOrder: IEnumMetaData<PickingPrintOrder>;
  IncludeAllLines: IEnumMetaData<IncludeAllLines>;
  QuantityToPick: IEnumMetaData<QuantityToPick>;
  TransactionNoteType: IEnumMetaData<TransactionNoteType>;
  BatchAllocationPromptBehaviour: IEnumMetaData<SalesOrderPromptBehaviour>;
  ShortagePromptBehaviour: IEnumMetaData<SalesOrderPromptBehaviour>;
  DefaultPriceBand: IEnumMetaData<Price | 'COST'>;
  Involves: IEnumMetaData<Involves>;
  EdiType: IEnumMetaData<EdiType>;
  DiscountType: IEnumMetaData<DiscountType>;
  Direction: IEnumMetaData<Direction>;
  WidgetWorkspace: IEnumMetaData<WidgetWorkspace>;
  AccountBalanceCriteria: IEnumMetaData<AccountBalanceCriteria>;
  OrderOrigin: IEnumMetaData<OrderOrigin>;
  SupplierPaymentPriority: IEnumMetaData<SupplierPaymentPriority>;
  RestockOption: IEnumMetaData<RestockOption>;
  AutomatedCreditControlActionType: IEnumMetaData<AutomatedCreditControlActionType>;
  AutomatedCreditControlTargetContact: IEnumMetaData<AutomatedCreditControlTargetContact>;
  NivoGraphPattern: IEnumMetaData<NivoGraphPattern>;
  NominalCompareOption: IEnumMetaData<NominalCompareOption>;
  PaymentInterval: IEnumMetaData<PaymentInterval>;
  SalesValueComparisonType: IEnumMetaData<SalesValueComparisonType>;
  CustomerPriceListVatInclusive: IEnumMetaData<CustomerPriceListVatInclusive>;
  CustomerSpecialPriceOrigin: IEnumMetaData<CustomerSpecialPriceOrigin>;
  PriceMatrixOrigin: IEnumMetaData<PriceMatrixOrigin>;
  CompanyUserTheme: IEnumMetaData<CompanyUserTheme>;
  SalesOrderActivationType: IEnumMetaData<SalesOrderActivationType>;
  CarrierIntegrationType: IEnumMetaData<CarrierIntegrationType>;
  SellingPriceBand: IEnumMetaData<Price>;
  CustomerContactDeliveryType: IEnumMetaData<CustomerContactDeliveryType>;
  CustomerStrategy: IEnumMetaData<CustomerStrategy>;
  WindsorFrameworkAtRiskStatus: IEnumMetaData<WindsorFrameworkAtRiskStatus>;
  ShipTheoryReferenceOption: IEnumMetaData<ShipTheoryReferenceOption>;
  PurchaseEdiType: IEnumMetaData<PurchaseEdiType>;
  EuCountry: IEnumMetaData<EuCountry>;
  PaymentReceiptAutoAllocateSequence: IEnumMetaData<PaymentReceiptAutoAllocateSequence>;
  StockCommitmentType: IEnumMetaData<StockCommitmentType>;
  DocumentSummaryStatus: IEnumMetaData<DocumentSummaryStatus>;
  VarianceApproval: IEnumMetaData<VarianceApproval>;
  MaxoptraOrderPriority: IEnumMetaData<MaxoptraOrderPriority>;
};

const fieldMetaData: IFieldMetaData = {
  AccountBalanceCriteria: {
    enumValues: [
      {
        value: AccountBalanceCriteria.All,
        label: 'All accounts',
      },
      {
        value: AccountBalanceCriteria.Outstanding,
        label: 'Outstanding balance',
      },
      {
        value: AccountBalanceCriteria.Month1,
        label: '30+ days balance',
      },
      {
        value: AccountBalanceCriteria.Month2,
        label: '60+ days balance',
      },
      {
        value: AccountBalanceCriteria.Month3,
        label: '90+ days balance',
      },
      {
        value: AccountBalanceCriteria.Month4,
        label: '120+ days balance',
      },
    ],
  },
  AccountType: {
    enumValues: [
      {
        value: AccountType.BalanceSheet,
        label: 'Balance Sheet',
      },
      {
        value: AccountType.ProfitLoss,
        label: 'Profit & Loss',
      },
    ],
  },
  AccrualPrepaymentType: {
    enumValues: [
      {
        value: AccrualPrepaymentType.Accrual,
        label: 'Accrual',
      },
      {
        value: AccrualPrepaymentType.Prepayment,
        label: 'Prepayment',
      },
    ],
  },
  ActionAllowed: {
    enumValues: [
      {
        value: ActionAllowed.Allowed,
        label: 'Allowed',
      },
      {
        value: ActionAllowed.Denied,
        label: 'Denied',
      },
      {
        value: ActionAllowed.Confirm,
        label: 'Confirm',
      },
    ],
  },
  ActionAllowedWarning: {
    //As Action Allowed, with additional warning option for temporary saves.
    enumValues: [
      {
        value: ActionAllowed.Allowed,
        label: 'Allowed',
      },
      {
        value: ActionAllowed.WarnAllowed,
        label: 'Warn / Allowed',
      },
      {
        value: ActionAllowed.Denied,
        label: 'Denied',
      },
      {
        value: ActionAllowed.Confirm,
        label: 'Confirm',
      },
      {
        value: ActionAllowed.WarnDenied,
        label: 'Warn / Denied',
      },
    ],
  },
  AlertType: {
    enumValues: [
      { value: AlertType.Info, label: 'Info' },
      { value: AlertType.Warning, label: 'Warning' },
      { value: AlertType.Error, label: 'Error' },
      { value: AlertType.Success, label: 'Success' },
    ],
  },
  AlertPosition: {
    enumValues: [
      { value: AlertPosition.Default, label: 'Default' },
      { value: AlertPosition.Block, label: 'Block' },
      { value: AlertPosition.Header, label: 'Header' },
    ],
  },
  AllocationMethod: {
    enumValues: [
      {
        value: AllocationMethod.NoAction,
        label: 'No Action',
      },
      {
        value: AllocationMethod.Automatic,
        label: 'Automatic',
      },
      {
        value: AllocationMethod.Manual,
        label: 'Manual',
      },
    ],
  },
  ArchivedType: {
    enumValues: [
      {
        value: ArchivedType.Manual,
        label: 'Manual',
      },
      {
        value: ArchivedType.Fulfilled,
        label: 'Fulfilled',
      },
      {
        value: ArchivedType.Converted,
        label: 'Converted',
      },
    ],
  },
  AttributeType: {
    enumValues: [
      {
        value: AttributeType.Reference,
        label: 'Reference',
      },
      {
        value: AttributeType.Text,
        label: 'Text',
      },
      {
        value: AttributeType.Integer,
        label: 'Numeric (Whole)',
      },
      {
        value: AttributeType.Numeric,
        label: 'Numeric (Decimal)',
      },
      {
        value: AttributeType.Date,
        label: 'Date',
      },
      {
        value: AttributeType.Grn,
        label: 'GRN',
      },
    ],
  },
  BankPaymentReceiptType: {
    enumValues: [
      {
        value: BankPaymentReceiptType.Receipt,
        label: 'Non-Vatable Receipt',
      },
      {
        value: BankPaymentReceiptType.VatableReceipt,
        label: 'Vatable Receipt',
      },
      {
        value: BankPaymentReceiptType.Payment,
        label: 'Non-Vatable Payment',
      },
      {
        value: BankPaymentReceiptType.VatablePayment,
        label: 'Vatable Payment',
      },
    ],
  },
  BankChargesAccount: {
    enumValues: [
      {
        value: BankChargesAccount.Receipt,
        label: 'Receipt Account',
      },
      {
        value: BankChargesAccount.Payment,
        label: 'Payment Account',
      },
    ],
  },
  BinaryActionAllowed: {
    enumValues: [
      {
        value: ActionAllowed.Allowed,
        label: 'Allowed',
      },
      {
        value: ActionAllowed.Denied,
        label: 'Denied',
      },
    ],
  },
  BinLocation: {
    location: {
      hint: 'A code representing the bin location',
    },
    pickSequence: {
      hint: 'The order the stock will be picked within the given warehouse',
    },
  },
  BinLocationSelection: {
    enumValues: [
      {
        value: BinLocationSelection.Automatic,
        label: 'Automatic',
      },
      {
        value: BinLocationSelection.Manual,
        label: 'Manual',
      },
    ],
  },
  BusinessRuleName: {
    enumValues: [
      {
        value: BusinessRuleName.QuoteAllowedForCustomer,
        label: 'Quote - Entry Rules',
      },
      {
        value: BusinessRuleName.OrderAllowedForCustomer,
        label: 'Sales Order - Entry Rules',
      },
      {
        value: BusinessRuleName.OrderSelectDeliveryMethod,
        label: 'Delivery Method Selection',
      },
      {
        value: BusinessRuleName.OrderLineAutomaticAllocation,
        label: 'Sales Order - Automatic Allocation Rules',
      },
      {
        value: BusinessRuleName.OrderAllowDispatch,
        label: 'Sales Order - Dispatch Rules',
      },
      {
        value: BusinessRuleName.OrderAllowAllocate,
        label: 'Sales Order - Allow Allocation Rules',
      },
      {
        value: BusinessRuleName.OrderAllowPick,
        label: 'Picking Note - Generation Rules',
      },
      {
        value: BusinessRuleName.OrderAllowDeliveryNote,
        label: 'Delivery Note - Generation Rules',
      },
      {
        value: BusinessRuleName.OrderApproveInvoice,
        label: 'Sales Order - Invoice Approval Rules',
      },
    ],
  },
  CarriageCalculationMethod: {
    enumValues: [
      {
        value: CarriageCalculationMethod.Calculated,
        label: 'Calculated',
      },
      {
        value: CarriageCalculationMethod.CalculatedOverride,
        label: 'Calculated Override',
      },
      {
        value: CarriageCalculationMethod.Manual,
        label: 'Manual',
      },
      {
        value: CarriageCalculationMethod.NoneRequired,
        label: 'None Required',
      },
    ],
  },
  CarriageCostCalculationMethod: {
    enumValues: [
      {
        value: CarriageCostCalculationMethod.Calculated,
        label: 'Calculated',
      },
      {
        value: CarriageCostCalculationMethod.Manual,
        label: 'Manual',
      },
    ],
  },
  CashReceivedType: {
    enumValues: [
      {
        value: CashReceivedType.CustomerDefault,
        label: 'Customer Default',
      },
      {
        value: CashReceivedType.True,
        label: 'True',
      },
      {
        value: CashReceivedType.False,
        label: 'False',
      },
    ],
  },
  CashStatus: {
    enumValues: [
      {
        value: CashStatus.None,
        label: 'None',
      },
      {
        value: CashStatus.CashWithOrder,
        label: 'Cash with Order',
      },
      {
        value: CashStatus.ProForma,
        label: 'Pro forma',
      },
    ],
  },
  ChargeBasis: {
    enumValues: [
      {
        value: ChargeBasis.Value,
        label: 'Value',
      },
      {
        value: ChargeBasis.Weight,
        label: 'Weight',
      },
    ],
  },
  WeightType: {
    enumValues: [
      {
        value: WeightType.Grams,
        label: 'Grams',
      },
      {
        value: WeightType.KiloGrams,
        label: 'Kilograms',
      },
    ],
  },
  WasteType: {
    enumValues: [
      {
        value: WasteType.HouseholdWaste,
        label: 'Household Waste',
      },
      {
        value: WasteType.StreetBinPackaging,
        label: 'Street Bin Packaging',
      },
      {
        value: WasteType.DrinksContainers,
        label: 'Drinks Containers',
      },
      {
        value: WasteType.PlasticBags,
        label: 'Plastic Bags',
      },
      {
        value: WasteType.ReuseablePackaging,
        label: 'Reuseable Packaging',
      },
      {
        value: WasteType.SelfManagedWaste,
        label: 'Self Managed Waste',
      },
      {
        value: WasteType.DrinksContainersBottle,
        label: 'Drinks Containers Bottle',
      },
      {
        value: WasteType.DrinksContainersLid,
        label: 'Drinks Containers Lid',
      },
    ],
  },
  EprPeriod: {
    enumValues: [
      {
        value: EprPeriod.Year,
        label: 'Year',
      },
      {
        value: EprPeriod.HalfYear,
        label: 'Half Year',
      },
      {
        value: EprPeriod.Quarter,
        label: 'Quarter',
      },
      {
        value: EprPeriod.FinancialPeriod,
        label: 'Financial Period',
      },
    ],
  },
  PackagingUse: {
    enumValues: [
      {
        value: PackagingUse.Primary,
        label: 'Primary',
      },
      {
        value: PackagingUse.Secondary,
        label: 'Secondary',
      },
      {
        value: PackagingUse.Tertiary,
        label: 'Tertiary',
      },
      {
        value: PackagingUse.Weee,
        label: 'Weee',
      },
      {
        value: PackagingUse.MaterialComposition,
        label: 'MaterialComposition',
      },
      {
        value: PackagingUse.Cbam,
        label: 'Cbam',
      },
    ],
  },
  CompanyFinancialPeriodStatus: {
    enumValues: [
      {
        value: FinancialPeriodStatus.Open,
        label: 'Open',
      },
      {
        value: FinancialPeriodStatus.Closed,
        label: 'Closed',
      },
      {
        value: FinancialPeriodStatus.Future,
        label: 'Future',
      },
    ],
  },
  ComparisonOperation: {
    enumValues: [
      {
        value: ComparisonOperation.LessThan,
        label: 'Less Than',
      },
      {
        value: ComparisonOperation.GreaterThan,
        label: 'Greater Than',
      },
      {
        value: ComparisonOperation.Equal,
        label: 'Equal To',
      },
    ],
  },
  CostPrice: {
    enumValues: [
      {
        value: CostPrice.Valuation,
        label: 'Valuation',
      },
      {
        value: CostPrice.Latest,
        label: 'Latest',
      },
    ],
  },
  Customer: {
    mandatoryCustomerOrderReference: {
      hint: 'Should orders for this customer have a mandatory customer order reference',
    },
  },
  CustomerAlertForm: {
    enumValues: [
      { value: CustomerAlertForm.SalesOrder, label: 'Sales Order' },
      { value: CustomerAlertForm.TradeCounter, label: 'Trade Counter' },
      { value: CustomerAlertForm.ManualDispatch, label: 'Manual Dispatch' },
      { value: CustomerAlertForm.Quote, label: 'Quote' },
      { value: CustomerAlertForm.SalesInvoice, label: 'Sales Invoice' },
      { value: CustomerAlertForm.SalesCredit, label: 'Sales Credit' },
      { value: CustomerAlertForm.SalesReceipt, label: 'Sales Receipt' },
    ],
  },
  CustomerContactDeliveryType: {
    enumValues: [
      {
        value: CustomerContactDeliveryType.Primary,
        label: 'Primary',
      },
      {
        value: CustomerContactDeliveryType.Allowed,
        label: 'Allowed',
      },
      {
        value: CustomerContactDeliveryType.NotAllowed,
        label: 'Not Allowed',
      },
    ],
  },
  CustomerPriceListVatInclusive: {
    enumValues: [
      {
        value: CustomerPriceListVatInclusive.Inclusive,
        label: 'Inclusive',
      },
      {
        value: CustomerPriceListVatInclusive.Exclusive,
        label: 'Exclusive',
      },
      {
        value: CustomerPriceListVatInclusive.CustomerDefault,
        label: 'Customer Default',
      },
    ],
  },
  CustomerSpecialPriceOrigin: {
    enumValues: [
      {
        value: CustomerSpecialPriceOrigin.Manual,
        label: 'Manual',
      },
      {
        value: CustomerSpecialPriceOrigin.Import,
        label: 'Import',
      },
      {
        value: CustomerSpecialPriceOrigin.BulkPriceApproval,
        label: 'Bulk Price Approval',
      },
      {
        value: CustomerSpecialPriceOrigin.Notification,
        label: 'Notification Action',
      },
    ],
  },
  CustomerSpecialPrices: {
    commencementDate: {
      hint: 'If left blank, these prices will be available immediately',
    },
    allowZero: {
      hint: 'Allow zero prices to be used during Sales; if turned off, the standard price band will be substituted for zero prices',
    },
  },
  CompanyUserTheme: {
    //'Dark' and 'DarkHighContrast' also exist, but are intentionally hidden from users - They are a WIP.
    enumValues: [
      {
        value: CompanyUserTheme.Light,
        label: 'Default',
      },
      {
        value: CompanyUserTheme.LightHighContrast,
        label: 'High Contrast',
      },
      {
        value: CompanyUserTheme.Dark,
        label: 'Dark (Experimental)',
      },
      {
        value: CompanyUserTheme.DarkHighContrast,
        label: 'Dark High Contrast (Experimental)',
      },
    ],
  },
  DashboardWidgetCalculation: {
    enumValues: [
      {
        value: DashboardWidgetCalculation.None,
        label: 'No Calculation',
      },
      {
        value: DashboardWidgetCalculation.Sum,
        label: 'Sum',
      },
      {
        value: DashboardWidgetCalculation.Divide,
        label: 'Divide (Primary / Secondary)',
      },
    ],
  },
  RagStatus: {
    enumValues: [
      {
        value: RagStatus.Green,
        label: 'Green',
      },
      {
        value: RagStatus.Amber,
        label: 'Amber',
      },
      {
        value: RagStatus.Red,
        label: 'Red',
      },
    ],
  },
  DayOfWeek: {
    enumValues: [
      {
        value: DayOfWeek.Monday,
        label: 'Monday',
      },
      {
        value: DayOfWeek.Tuesday,
        label: 'Tuesday',
      },
      {
        value: DayOfWeek.Wednesday,
        label: 'Wednesday',
      },
      {
        value: DayOfWeek.Thursday,
        label: 'Thursday',
      },
      {
        value: DayOfWeek.Friday,
        label: 'Friday',
      },
      {
        value: DayOfWeek.Saturday,
        label: 'Saturday',
      },
      {
        value: DayOfWeek.Sunday,
        label: 'Sunday',
      },
    ],
  },
  DefaultPriceBand: {
    enumValues: [
      {
        value: 'COST',
        label: 'Cost Price',
      },
      {
        value: Price.SalesPriceMinimum,
        label: 'Minimum Price',
      },
      {
        value: Price.SalesPrice_1,
        label: 'Price 1',
      },
      {
        value: Price.SalesPrice_2,
        label: 'Price 2',
      },
      {
        value: Price.SalesPrice_3,
        label: 'Price 3',
      },
      {
        value: Price.SalesPrice_4,
        label: 'Price 4',
      },
      {
        value: Price.SalesPrice_5,
        label: 'Price 5',
      },
      {
        value: Price.SalesPrice_6,
        label: 'Price 6',
      },
      {
        value: Price.SalesPrice_7,
        label: 'Price 7',
      },
      {
        value: Price.SalesPrice_8,
        label: 'Price 8',
      },
      {
        value: Price.SalesPrice_9,
        label: 'Price 9',
      },
      {
        value: Price.SalesPrice_10,
        label: 'Price 10',
      },
    ],
  },
  SellingPriceBand: {
    enumValues: [
      {
        value: Price.SalesPrice_1,
        label: 'Selling Price 1',
      },
      {
        value: Price.SalesPrice_2,
        label: 'Selling Price 2',
      },
      {
        value: Price.SalesPrice_3,
        label: 'Selling Price 3',
      },
      {
        value: Price.SalesPrice_4,
        label: 'Selling Price 4',
      },
      {
        value: Price.SalesPrice_5,
        label: 'Selling Price 5',
      },
      {
        value: Price.SalesPrice_6,
        label: 'Selling Price 6',
      },
      {
        value: Price.SalesPrice_7,
        label: 'Selling Price 7',
      },
      {
        value: Price.SalesPrice_8,
        label: 'Selling Price 8',
      },
      {
        value: Price.SalesPrice_9,
        label: 'Selling Price 9',
      },
      {
        value: Price.SalesPrice_10,
        label: 'Selling Price 10',
      },
    ],
  },
  Direction: {
    enumValues: [
      { value: Direction.Asc, label: 'Ascending' },
      { value: Direction.Desc, label: 'Descending' },
    ],
  },
  DirectDebitFrequency: {
    enumValues: [
      { value: DirectDebitFrequency.Monthly, label: 'Monthly' },
      { value: DirectDebitFrequency.OnHold, label: 'On Hold' },
      { value: DirectDebitFrequency.Weekly, label: 'Weekly' },
      { value: DirectDebitFrequency.Quarterly, label: 'Quarterly' },
      { value: DirectDebitFrequency.Yearly, label: 'Yearly' },
    ],
  },
  DirectDebitRequestOutputFormat: {
    enumValues: [
      { value: DirectDebitRequestOutputFormat.Microbank, label: 'Microbank' },
      { value: DirectDebitRequestOutputFormat.Bacs, label: 'BACS' },
    ],
  },
  DiscountApplicationMethod: {
    enumValues: [
      {
        value: DiscountApplicationMethod.Discount,
        label: 'Discount',
      },
      {
        value: DiscountApplicationMethod.NettPrice,
        label: 'Net Price',
      },
    ],
  },
  DispatchRule: {
    enumValues: [
      {
        value: DispatchRule.NoRule,
        label: 'No Restrictions',
      },
      {
        value: DispatchRule.DispatchCompleteOrdersOnly,
        label: 'Dispatch Complete Orders Only',
      },
      {
        value: DispatchRule.DispatchCompleteOrderLinesOnly,
        label: 'Dispatch Complete Lines Only',
      },
    ],
  },
  DocumentStatus: {
    enumValues: [
      {
        value: DocumentStatus.Draft,
        label: 'Draft',
      },
      {
        value: DocumentStatus.Pending,
        label: 'Pending',
      },
      {
        value: DocumentStatus.Posted,
        label: 'Posted',
      },
      {
        value: DocumentStatus.Reinvoiced,
        label: 'Re-Invoiced',
      },
      {
        value: DocumentStatus.Credited,
        label: 'Credited',
      },
      {
        value: DocumentStatus.Reversed,
        label: 'Reversed',
      },
    ],
  },
  DocumentType: {
    enumValues: [
      {
        value: DocumentType.SalesInvoice,
        label: 'Sales Invoice',
      },
      {
        value: DocumentType.SalesReceipt,
        label: 'Sales Receipt',
      },
      {
        value: DocumentType.SalesRefund,
        label: 'Sales Refund',
      },
      {
        value: DocumentType.SalesCredit,
        label: 'Sales Credit',
      },
      {
        value: DocumentType.StockTransfer,
        label: 'Stock Transfer',
      },
      {
        value: DocumentType.StockAdjustment,
        label: 'Stock Adjustment',
      },
      {
        value: DocumentType.GoodsReceipt,
        label: 'Goods Receipt',
      },
      {
        value: DocumentType.StockTake,
        label: 'Stock Take',
      },
      {
        value: DocumentType.Allocation,
        label: 'Sales Order',
      },
      {
        value: DocumentType.DispatchInstruction,
        label: 'Dispatch Instruction',
      },
      {
        value: DocumentType.PurchaseOrderReceiptAdjustment,
        label: 'Purchase Order Receipt Adj',
      },
      {
        value: DocumentType.SalesOpeningBalanceInvoice,
        label: 'Sales OB Invoice',
      },
      {
        value: DocumentType.SalesOpeningBalanceCredit,
        label: 'Sales OB Credit',
      },
      {
        value: DocumentType.SalesBadDebt,
        label: 'Sales Bad Debt',
      },
      {
        value: DocumentType.NominalJournal,
        label: 'Nominal Journal',
      },
      {
        value: DocumentType.SalesInvoice,
        label: 'Customer Invoice',
      },
      {
        value: DocumentType.NominalAccrual,
        label: 'Nominal Accrual',
      },
      {
        value: DocumentType.NominalPrepayment,
        label: 'Nominal Prepayment',
      },
      {
        value: DocumentType.BankPayment,
        label: 'Bank Payment',
      },
      {
        value: DocumentType.BankReceipt,
        label: 'Bank Receipt',
      },
      {
        value: DocumentType.PurchaseInvoice,
        label: 'Purchase Invoice',
      },
      {
        value: DocumentType.PurchasePayment,
        label: 'Purchase Payment',
      },
      {
        value: DocumentType.PurchaseRefund,
        label: 'Purchase Refund',
      },
      {
        value: DocumentType.PurchaseCredit,
        label: 'Purchase Credit',
      },
      {
        value: DocumentType.PurchaseOpeningBalanceInvoice,
        label: 'Purchase OB Invoice',
      },
      {
        value: DocumentType.PurchaseOpeningBalanceCredit,
        label: 'Purchase OB Credit',
      },
      {
        value: DocumentType.SalesVatInvoice,
        label: 'Sales VAT Invoice',
      },
      {
        value: DocumentType.SalesVatCredit,
        label: 'Sales VAT Credit',
      },
      {
        value: DocumentType.PurchaseVatInvoice,
        label: 'Purchase VAT Invoice',
      },
      {
        value: DocumentType.PurchaseVatCredit,
        label: 'Purchase VAT Credit',
      },
      {
        value: DocumentType.BankTransfer,
        label: 'Bank Transfer',
      },
      {
        value: DocumentType.SalesAllocation,
        label: 'Sales Allocation',
      },
      {
        value: DocumentType.BankRevaluation,
        label: 'Bank Revaluation',
      },
      {
        value: DocumentType.PurchaseAllocation,
        label: 'Purchase Allocation',
      },
      {
        value: DocumentType.CostAdjustment,
        label: 'Cost Adjustment',
      },
      {
        value: DocumentType.DirectDebitLetter,
        label: 'Direct Debit Letter',
      },
      {
        value: DocumentType.SalesLedgerJournal,
        label: 'Sales Journal',
      },
      {
        value: DocumentType.PurchaseLedgerJournal,
        label: 'Purchase Journal',
      },
      {
        value: DocumentType.SalesImport,
        label: 'Sales Import',
      },
      {
        value: DocumentType.PurchaseImport,
        label: 'Purchase Import',
      },
    ],
  },
  DocumentOutputType: {
    // ReportOutputType - Reduced to only necessary options.
    enumValues: [
      {
        value: ReportOutputType.Email,
        label: 'E-Mail',
      },
      {
        value: ReportOutputType.EmailWithPreview,
        label: 'E-Mail with Preview',
      },
      {
        value: ReportOutputType.LocalPrinter,
        label: 'Local Printer',
      },
      {
        value: ReportOutputType.SystemPrinter,
        label: 'System Printer',
      },
    ],
  },
  EdiType: {
    enumValues: [
      {
        value: EdiType.Edi,
        label: 'EDI',
      },
      {
        value: EdiType.Xedi,
        label: 'XEDI',
      },
    ],
  },
  EuCountry: {
    enumValues: Object.entries(EuCountry).map(([key, value]) => {
      return {
        value: value,
        label: key,
      };
    }),
  },
  PurchaseEdiType: {
    enumValues: [
      {
        value: PurchaseEdiType.Ogl,
        label: 'OGL',
      },
      {
        value: PurchaseEdiType.Xedi,
        label: 'XEDI',
      },
      {
        value: PurchaseEdiType.Edi,
        label: 'EDI',
      },
      {
        value: PurchaseEdiType.Simplexml,
        label: 'Simple XML',
      },
    ],
  },
  Entity: {
    enumValues: [
      {
        value: Entity.Customer,
        label: 'Customer',
      },
      {
        value: Entity.SalesOpeningBalance,
        label: 'Customer Opening Balance',
      },
      {
        value: Entity.CustomerPriceMatrix,
        label: 'Customer Price Matrix',
      },
      {
        value: Entity.CustomerNote,
        label: 'Customer Note',
      },
      {
        value: Entity.CustomerSpecialPrice,
        label: 'Customer Special Price',
      },
      {
        value: Entity.PromotionalPrice,
        label: 'Promotional Price',
      },
      {
        value: Entity.SalesTransaction,
        label: 'Customer Transaction History',
      },
      {
        value: Entity.Product,
        label: 'Product',
      },
      {
        value: Entity.ProductPriceMatrix,
        label: 'Product Price Matrix',
      },
      {
        value: Entity.ProductNote,
        label: 'Product Note',
      },
      {
        value: Entity.ProductCategory,
        label: 'Product Category',
      },
      {
        value: Entity.ChannelProduct,
        label: 'Product Channel Alias',
      },
      {
        value: Entity.SalesOrder,
        label: 'Sales Order',
      },
      {
        value: Entity.SalesOrderNote,
        label: 'Sales Order Note',
      },
      {
        value: Entity.SalesInvoice,
        label: 'Sales Invoice',
      },
      {
        value: Entity.SalesCredit,
        label: 'Sales Credit',
      },
      {
        value: Entity.Stock,
        label: 'Stock',
      },
      {
        value: Entity.StockTransaction,
        label: 'Stock Transaction',
      },
      {
        value: Entity.Supplier,
        label: 'Supplier',
      },
      {
        value: Entity.SupplierNote,
        label: 'Supplier Note',
      },
      {
        value: Entity.PurchaseOpeningBalance,
        label: 'Supplier Opening Balance',
      },
      {
        value: Entity.PurchaseTransaction,
        label: 'Supplier Transaction History',
      },
      {
        value: Entity.Warehouse,
        label: 'Warehouse',
      },
      {
        value: Entity.ProductWarehouseDetail,
        label: 'Product Warehouse Details',
      },
      {
        value: Entity.PurchaseOrder,
        label: 'Purchase Order',
      },
      {
        value: Entity.CustomerContact,
        label: 'Customer Contact',
      },
      {
        value: Entity.SupplierContact,
        label: 'Supplier Contact',
      },
      {
        value: Entity.CustomerPartNumber,
        label: 'Customer Part Number',
      },
      {
        value: Entity.BinLocation,
        label: 'Bin Location',
      },
      {
        value: Entity.WebPage,
        label: 'Web Page',
      },
      {
        value: Entity.Alert,
        label: 'Alert',
      },
      {
        value: Entity.SupplierPriceMatrix,
        label: 'Supplier Price Matrix',
      },
      {
        value: Entity.Nominal,
        label: 'Nominal',
      },
      {
        value: Entity.StockTakeLine,
        label: 'Stock Take Line',
      },
      {
        value: Entity.DiaryEvent,
        label: 'Diary Event',
      },
      {
        value: Entity.DiaryNote,
        label: 'Diary Note',
      },
      {
        value: Entity.StockTransfer,
        label: 'Stock Transfer',
      },
      {
        value: Entity.DeliveryRoute,
        label: 'Delivery Route',
      },
      {
        value: Entity.HistoricalDataSheet,
        label: 'Historical Data Sheet',
      },
      {
        value: Entity.CarriageCost,
        label: 'Carriage Cost',
      },
      {
        value: Entity.ProductMaterialDetail,
        label: 'Product Material Detail (EPR)',
      },
    ],
  },
  Event: {
    createdOn: {
      hint: 'The date this event was created',
    },
  },
  FinancialCalendarType: {
    enumValues: [
      {
        value: FinancialCalendarType.Calendar,
        label: 'Calendar',
      },
      {
        value: FinancialCalendarType.Fourfourfive,
        label: '4-4-5',
      },
    ],
  },
  FinancialPeriodStatus: {
    enumValues: [
      {
        value: FinancialPeriodStatus.Open,
        label: 'Open',
      },
      {
        value: FinancialPeriodStatus.Closed,
        label: 'Closed',
      },
      {
        value: FinancialPeriodStatus.Future,
        label: 'Future',
      },
    ],
  },
  Frequency: {
    enumValues: [
      {
        value: Frequency.Daily,
        label: 'Days',
      },
      {
        value: Frequency.Weekly,
        label: 'Weeks',
      },
      {
        value: Frequency.Monthly,
        label: 'Months',
      },
    ],
  },
  GoodsReceiptOrigin: {
    enumValues: [
      {
        value: GoodsReceiptOrigin.Manual,
        label: 'Manual',
      },
      {
        value: GoodsReceiptOrigin.PurchaseOrder,
        label: 'Purchase Order',
      },
    ],
  },
  IsoCurrency: {
    codeAlpha3: {
      title: 'ISO Currency Code',
    },
  },
  Involves: {
    enumValues: [
      {
        value: Involves.Customer,
        label: 'Customer',
      },
      {
        value: Involves.Supplier,
        label: 'Supplier',
      },
      {
        value: Involves.None,
        label: 'None',
      },
    ],
  },
  InvoiceConsolidationOption: {
    enumValues: [
      {
        value: InvoiceConsolidationOption.None,
        label: 'None',
      },
      {
        value: InvoiceConsolidationOption.Consolidated,
        label: 'Consolidated by Customer',
      },
      {
        value: InvoiceConsolidationOption.ConsolidatedIncOrderRef,
        label: 'Consolidated by Customer & Order Reference',
      },
    ],
  },
  IssueReceipt: {
    enumValues: [
      {
        value: IssueReceipt.Issue,
        label: 'Issue',
      },
      {
        value: IssueReceipt.Receipt,
        label: 'Receipt',
      },
    ],
  },
  JournalType: {
    enumValues: [
      {
        value: JournalType.Single,
        label: 'Single Entry',
      },
      {
        value: JournalType.Reversing,
        label: 'Reversing Entry',
      },
      {
        value: JournalType.Recurring,
        label: 'Recurring Entry',
      },
      {
        value: JournalType.Vatable,
        label: 'Vatable',
      },
    ],
  },
  JournalVatType: {
    enumValues: [
      {
        value: JournalVatType.NotApplicable,
        label: 'N/A',
      },
      {
        value: JournalVatType.InputGoods,
        label: 'Input Goods',
      },
      {
        value: JournalVatType.InputVat,
        label: 'Input VAT',
      },
      {
        value: JournalVatType.OutputGoods,
        label: 'Output Goods',
      },
      {
        value: JournalVatType.OutputVat,
        label: 'Output VAT',
      },
    ],
  },
  LengthUnitOfMeasure: {
    enumValues: [
      {
        value: LengthUnitOfMeasure.Mm,
        label: 'Millimetres',
      },
      {
        value: LengthUnitOfMeasure.Cm,
        label: 'Centimetres',
      },
      {
        value: LengthUnitOfMeasure.M,
        label: 'Metres',
      },
    ],
  },
  MarginRecalculationMethod: {
    enumValues: [
      {
        value: MarginRecalculationMethod.SellingPrice,
        label: 'Selling Price',
      },
      {
        value: MarginRecalculationMethod.DiscountTerms,
        label: 'Discount Terms',
      },
    ],
  },
  MaxoptraOption: {
    enumValues: [
      {
        value: MaxoptraOption.Create,
        label: 'Create',
      },
      {
        value: MaxoptraOption.Update,
        label: 'Update',
      },
    ],
  },
  MaxoptraOrderPriority: {
    enumValues: [
      {
        value: MaxoptraOrderPriority.Normal,
        label: 'Normal',
      },
      {
        value: MaxoptraOrderPriority.Medium,
        label: 'Medium',
      },
      {
        value: MaxoptraOrderPriority.High,
        label: 'High',
      },
    ],
  },
  MaxoptraOrderStatus: {
    enumValues: [
      {
        value: MaxoptraOrderStatus.Accepted,
        label: 'Accepted',
      },
      {
        value: MaxoptraOrderStatus.Allocated,
        label: 'Allocated',
      },
      {
        value: MaxoptraOrderStatus.Arrived,
        label: 'Arrived',
      },
      {
        value: MaxoptraOrderStatus.Completed,
        label: 'Completed',
      },
      {
        value: MaxoptraOrderStatus.DeliveryStarted,
        label: 'Delivery Started',
      },
      {
        value: MaxoptraOrderStatus.Driving,
        label: 'Driving',
      },
      {
        value: MaxoptraOrderStatus.Failed,
        label: 'Failed',
      },
      {
        value: MaxoptraOrderStatus.Unallocated,
        label: 'Unallocated',
      },
    ],
  },
  MinMaxCalculationMethod: {
    enumValues: [
      {
        value: MinMaxCalculationMethod.Sales,
        label: 'Based on Sales',
      },
      {
        value: MinMaxCalculationMethod.Purchases,
        label: 'Based on Purchases',
      },
    ],
  },
  MonthlyOccurrence: {
    enumValues: [
      {
        value: MonthlyOccurrence.First,
        label: 'First',
      },
      {
        value: MonthlyOccurrence.Second,
        label: 'Second',
      },
      {
        value: MonthlyOccurrence.Third,
        label: 'Third',
      },
      {
        value: MonthlyOccurrence.Fourth,
        label: 'Fourth',
      },
      {
        value: MonthlyOccurrence.Last,
        label: 'Last',
      },
    ],
  },
  NivoGraphPattern: {
    enumValues: [
      {
        value: NivoGraphPattern.None,
        label: 'None',
      },
      {
        value: NivoGraphPattern.Dots,
        label: 'Dots',
      },
      {
        value: NivoGraphPattern.Lines,
        label: 'Lines',
      },
      {
        value: NivoGraphPattern.Squares,
        label: 'Squares',
      },
    ],
  },
  Nominal: {
    allowDirectPostings: {
      hint: 'Should users be able to post directly to this account or is it a control account',
    },
    postingSuspended: {
      hint: 'Stops any transactions being posted to the account',
    },
  },
  NominalCompareOption: {
    enumValues: [
      {
        value: NominalCompareOption.None,
        label: 'None',
      },
      {
        value: NominalCompareOption.Budget,
        label: 'Budget',
      },
      {
        value: NominalCompareOption.PrevPeriod,
        label: 'Previous Period',
      },
      {
        value: NominalCompareOption.PrevLyPeriod,
        label: 'Previous Period Last Year',
      },
      {
        value: NominalCompareOption.PrevYear,
        label: 'Previous Year',
      },
    ],
  },
  NotificationType: {
    enumValues: [
      {
        value: NotificationType.Off,
        label: 'Off',
      },
      {
        value: NotificationType.Temporary,
        label: 'Temporary',
      },
      {
        value: NotificationType.Persistent,
        label: 'Persistent',
      },
    ],
  },
  NumericConfig: {
    enumValues: [
      {
        value: NumericConfig.Manual,
        label: 'Manual',
      },
      {
        value: NumericConfig.AutoGenerated,
        label: 'Auto Generated',
      },
      {
        value: NumericConfig.AutoGeneratedOverride,
        label: 'Auto Generated with Override',
      },
    ],
  },
  OrderConfig: {
    mandatoryCustomerOrderReference: {
      hint: 'Should every order require a customer order reference?',
    },
  },
  OpeningBalanceDocumentType: {
    enumValues: [
      {
        value: DocumentType.SalesOpeningBalanceInvoice,
        label: 'OB Invoice',
      },
      {
        value: DocumentType.SalesOpeningBalanceCredit,
        label: 'OB Credit',
      },
    ],
  },
  PickingPrintOrder: {
    enumValues: [
      {
        value: PickingPrintOrder.Default,
        label: 'Default',
      },
      {
        value: PickingPrintOrder.BinLocationSequence,
        label: 'Bin location sequence',
      },
    ],
  },
  IncludeAllLines: {
    enumValues: [
      {
        value: IncludeAllLines.Never,
        label: 'Never',
      },
      {
        value: IncludeAllLines.Always,
        label: 'Always',
      },
      {
        value: IncludeAllLines.FirstPickOnly,
        label: 'First Pick Only',
      },
    ],
  },
  PurchaseOpeningBalanceDocumentType: {
    enumValues: [
      {
        value: DocumentType.PurchaseOpeningBalanceInvoice,
        label: 'OB Invoice',
      },
      {
        value: DocumentType.PurchaseOpeningBalanceCredit,
        label: 'OB Credit',
      },
    ],
  },
  PurchaseOrderLineStatus: {
    enumValues: [
      {
        value: PurchaseOrderLineStatus.AwaitingDelivery,
        label: 'Awaiting Delivery',
      },
      {
        value: PurchaseOrderLineStatus.Assigned,
        label: 'Assigned',
      },
      {
        value: PurchaseOrderLineStatus.Received,
        label: 'Received',
      },
      {
        value: PurchaseOrderLineStatus.Invoiced,
        label: 'Invoiced',
      },
      {
        value: PurchaseOrderLineStatus.ConsideredComplete,
        label: 'Considered Complete',
      },
    ],
  },
  PayByLinkPaymentStatus: {
    enumValues: [
      {
        value: PayByLinkPaymentStatus.Pending,
        label: 'Pending',
      },
      {
        value: PayByLinkPaymentStatus.PartPaid,
        label: 'Part Paid',
      },
      {
        value: PayByLinkPaymentStatus.Paid,
        label: 'Paid',
      },
      {
        value: PayByLinkPaymentStatus.Expired,
        label: 'EXPIRED',
      },
    ],
  },
  PaymentInterval: {
    enumValues: [
      {
        value: PaymentInterval.Days,
        label: 'Days',
      },
      {
        value: PaymentInterval.Months,
        label: 'Months',
      },
      {
        value: PaymentInterval.MonthsPlus,
        label: 'Months (End of Month)',
      },
    ],
  },
  PaymentType: {
    enumValues: [
      {
        value: PaymentType.Cash,
        label: 'Cash',
      },
      {
        value: PaymentType.Cheque,
        label: 'Cheque',
      },
      {
        value: PaymentType.CardCustomerPresent,
        label: 'Card Customer Present',
      },
      {
        value: PaymentType.CardCustomerNotPresent,
        label: 'Card Customer Not Present',
      },
      {
        value: PaymentType.ManualCreditNote,
        label: 'Manual Credit Note',
      },
      {
        value: PaymentType.CreditNote,
        label: 'Credit Note',
      },
      {
        value: PaymentType.Moto,
        label: 'MOTO',
      },
      {
        value: PaymentType.PayByLink,
        label: 'Pay By Link',
      },
      {
        value: PaymentType.Pac,
        label: 'Pay at Counter',
      },
    ],
  },
  PaymentTermDate: {
    enumValues: [
      {
        value: PaymentTermDate.Calendar,
        label: 'Calendar Monthly',
      },
      {
        value: PaymentTermDate.Document,
        label: 'Document Date',
      },
      {
        value: PaymentTermDate.PeriodStart,
        label: 'Start of Month',
      },
      {
        value: PaymentTermDate.PeriodEnd,
        label: 'End of Month',
      },
    ],
  },
  PaymentStatus: {
    enumValues: [
      {
        value: PaymentStatus.Outstanding,
        label: 'Outstanding',
      },
      {
        value: PaymentStatus.Partpaid,
        label: 'Part Paid',
      },
      {
        value: PaymentStatus.Paid,
        label: 'Paid',
      },
      {
        value: PaymentStatus.PartRefunded,
        label: 'Part Refunded',
      },
      {
        value: PaymentStatus.Refunded,
        label: 'Refunded',
      },
    ],
  },
  PaymentReceiptAutoAllocateSequence: {
    enumValues: [
      {
        value: PaymentReceiptAutoAllocateSequence.ExcludeCredits,
        label: 'Exclude Credits',
      },
      {
        value: PaymentReceiptAutoAllocateSequence.DateSequence,
        label: 'Due Date Sequence',
      },
      {
        value: PaymentReceiptAutoAllocateSequence.CreditsFirst,
        label: 'Credits First',
      },
    ],
  },
  PickingSelection: {
    enumValues: [
      {
        value: PickingSelection.NotSet,
        label: 'No Restrictions',
      },
      {
        value: PickingSelection.PickFullyAllocatedLines,
        label: 'Pick Fully Allocated Lines Only',
      },
      {
        value: PickingSelection.PickFullyAllocatedOrders,
        label: 'Pick Fully Allocated Orders Only',
      },
    ],
  },
  PickingGroupSelection: {
    enumValues: [
      {
        value: PickingGroupSelection.BinLocation,
        label: 'Bin Location',
      },
      {
        value: PickingGroupSelection.StockLot,
        label: 'Lot',
      },
    ],
  },
  DeliveryNoteRule: {
    enumValues: [
      {
        value: DeliveryNoteRule.EveryItem,
        label: 'Every Item',
      },
      {
        value: DeliveryNoteRule.NoRule,
        label: 'Dispatched Lines Only',
      },
      {
        value: DeliveryNoteRule.DeliveryNotePrintFullyDispatchedLinesOnly,
        label: 'Fully Dispatched Lines Only',
      },
      {
        value: DeliveryNoteRule.DeliveryNotePrintFullyDispatchedOrdersOnly,
        label: 'Fully Dispatched Orders Only',
      },
      {
        value: DeliveryNoteRule.EveryItemFollowedByDispatchedLinesOnly,
        label: 'Every Item followed by Dispatched Lines Only',
      },
    ],
  },
  ProductOrigin: {
    enumValues: [
      {
        value: ProductOrigin.Manual,
        label: 'Manual',
      },
      {
        value: ProductOrigin.Import,
        label: 'Import',
      },
      {
        value: ProductOrigin.Special,
        label: 'Special',
      },
      {
        value: ProductOrigin.Luckins,
        label: 'Luckins',
      },
    ],
  },
  PriceUpdate: {
    enumValues: [
      {
        value: PriceUpdate.Never,
        label: 'Never',
      },
      {
        value: PriceUpdate.Always,
        label: 'Always',
      },
      {
        value: PriceUpdate.OncePerOrder,
        label: 'Once per Order',
      },
    ],
  },
  Product: {
    costUnitQuantity: {
      hint: 'The quantity to which cost prices relate',
    },
    costUnitDescription: {
      hint: 'Description of the quantity to which cost prices relate',
    },
  },
  ProductAlertForm: {
    enumValues: [
      { value: ProductAlertForm.SalesOrder, label: 'Sales Order' },
      { value: ProductAlertForm.Quote, label: 'Quote' },
      { value: ProductAlertForm.TradeCounter, label: 'Trade Counter' },
      { value: ProductAlertForm.ManualDispatch, label: 'Manual Dispatch' },
      { value: ProductAlertForm.SalesInvoice, label: 'Sales Invoice' },
      { value: ProductAlertForm.SalesCredit, label: 'Sales Credit' },
      { value: ProductAlertForm.PurchaseOrder, label: 'Purchase Order' },
      { value: ProductAlertForm.PurchaseInvoice, label: 'Purchase Invoice' },
      { value: ProductAlertForm.PurchaseCredit, label: 'Purchase Credit' },
      { value: ProductAlertForm.PurchaseOrderReceipt, label: 'Purchase Order Receipt' },
      { value: ProductAlertForm.SupplierReturn, label: 'Supplier Return' },
    ],
  },
  ProductClass: {
    enumValues: [
      {
        value: ProductClass.Standard,
        label: 'Standard',
      },
      {
        value: ProductClass.Process,
        label: 'Process',
      },
      {
        value: ProductClass.Kit,
        label: 'Kit',
      },
      {
        value: ProductClass.Assembly,
        label: 'Assembly',
      },
      {
        value: ProductClass.FreeType,
        label: 'FreeType',
      },
    ],
  },
  ProductAttributeValue: {
    excludeFromWorkspace: true,
  },
  DiaryAttributeValue: {
    excludeFromWorkspace: true,
  },
  CustomerAttributeValue: {
    excludeFromWorkspace: true,
  },
  SupplierAttributeValue: {
    excludeFromWorkspace: true,
  },
  SalesAttributeValue: {
    excludeFromWorkspace: true,
  },
  PurchaseAttributeValue: {
    excludeFromWorkspace: true,
  },
  ProductWarehouseDetail: {
    excludeFromWorkspace: true,
  },
  ProductAttributeValueGlobalAmend: {
    excludeFromWorkspace: true,
  },
  DiaryAttributeValueGlobalAmend: {
    excludeFromWorkspace: true,
  },
  CustomerAttributeValueGlobalAmend: {
    excludeFromWorkspace: true,
  },
  SupplierAttributeValueGlobalAmend: {
    excludeFromWorkspace: true,
  },
  ProductConfig: {
    referenceFormatInUse: {
      hint: 'Should product references adhere to a format?',
    },
    canReuseDeleted: {
      hint: 'Can you assign a previously deleted product reference',
    },
  },
  PurchaseOrderMatchingType: {
    enumValues: [
      {
        value: PurchaseOrderMatchingType.PurchaseOrder,
        label: 'Purchase Order',
      },
      {
        value: PurchaseOrderMatchingType.PurchaseOrderAndGrn,
        label: 'Goods Receipts',
      },
    ],
  },
  PurchasePaymentStatus: {
    enumValues: [
      {
        value: PurchasePaymentStatus.None,
        label: 'None',
      },
      {
        value: PurchasePaymentStatus.Disputed,
        label: 'Disputed',
      },
    ],
  },
  RecurrenceType: {
    enumValues: [
      {
        value: RecurrenceType.Fixed,
        label: 'Fixed Frequency',
      },
      {
        value: RecurrenceType.Weekly,
        label: 'Weekly on these days',
      },
      {
        value: RecurrenceType.Monthly,
        label: 'Monthly on the...',
      },
    ],
  },
  ReportOutputType: {
    enumValues: [
      {
        value: ReportOutputType.Pdf,
        label: 'PDF Document',
      },
      {
        value: ReportOutputType.Docx,
        label: 'DOCX Document',
      },
      {
        value: ReportOutputType.Xlsx,
        label: 'XLSX Spreadsheet',
      },
      {
        value: ReportOutputType.Csv,
        label: 'CSV File',
      },
      {
        value: ReportOutputType.Email,
        label: 'E-Mail',
      },
      {
        value: ReportOutputType.EmailWithPreview,
        label: 'E-Mail with Preview',
      },
      {
        value: ReportOutputType.LocalPrinter,
        label: 'Local Printer',
      },
      {
        value: ReportOutputType.SystemPrinter,
        label: 'System Printer',
      },
    ],
  },
  ReportType: {
    enumValues: [
      {
        value: ReportType.CustomerContactLetter,
        label: 'Customer Contact Letter',
        mergeFields: customerContactDocument,
      },
      {
        value: ReportType.CustomerContactMailshot,
        label: 'Customer Contact Mailshot',
        mergeFields: customerContactDocument,
      },
      {
        value: ReportType.CustomerContactLabel,
        label: 'Customer Contact Label',
        mergeFields: customerContactDocument,
      },
      {
        value: ReportType.SalesAcknowledgement,
        recordClass: 'p4.entities.sales.AcknowledgementLine',
        label: 'Sales Acknowledgement',
      },
      {
        value: ReportType.SalesQuote,
        label: 'Quotation',
      },
      {
        value: ReportType.AgedCreditReport,
        label: 'Aged Creditor Report',
      },
      {
        value: ReportType.AgedCreditTransactionReport,
        label: 'Aged Creditor Transaction Report',
      },
      {
        value: ReportType.AgedDebtReport,
        label: 'Aged Debt Report',
      },
      {
        value: ReportType.AgedDebtTransactionReport,
        label: 'Aged Debt Transaction Report',
      },
      {
        value: ReportType.AllocationAuditReport,
        label: 'Allocation Audit Report',
      },
      {
        value: ReportType.VatAuditReport,
        label: 'VAT Audit Report',
      },
      {
        value: ReportType.VatAuditTransactionReport,
        label: 'VAT Audit Transaction Report',
      },
      {
        value: ReportType.SalesPickingList,
        label: 'Picking Instruction',
      },
      {
        value: ReportType.SalesDeliveryNote,
        label: 'Delivery Note',
      },
      {
        value: ReportType.ConformityCertificate,
        label: 'Certificate of Conformity',
      },
      {
        value: ReportType.CustomerStatementReport,
        label: 'Customer Statement Report',
      },
      {
        value: ReportType.CustomerPriceList,
        label: 'Customer Price List',
      },
      {
        value: ReportType.StockTransferReport,
        label: 'Stock Transfer Report',
      },
      {
        value: ReportType.ProprietaryLabel,
        label: 'Proprietary Label',
      },
      {
        value: ReportType.StockValuationReport,
        label: 'Stock Valuation Report',
      },
      {
        value: ReportType.StockTakeReport,
        label: 'Stock Take Report',
      },
      {
        value: ReportType.SalesInvoice,
        label: 'Sales Invoice',
      },
      {
        value: ReportType.CommercialInvoice,
        label: 'Commercial Invoice',
      },
      {
        value: ReportType.PurchaseOrderDocument,
        label: 'Purchase Order Document',
      },
      {
        value: ReportType.DebitNote,
        label: 'Debit Note',
      },
      {
        value: ReportType.ProformaInvoice,
        label: 'Proforma Invoice',
      },
      {
        value: ReportType.SupplierPriceList,
        label: 'Supplier Price List',
      },
      {
        value: ReportType.SupplierRemittance,
        label: 'Supplier Remittance Advice',
      },
      {
        value: ReportType.DataSheet,
        label: 'Data Sheet',
      },
      {
        value: ReportType.DispatchEmail,
        label: 'Dispatch Email',
        mergeFields: [
          {
            key: 'salesOrder.customer.name',
            text: 'Customer Name',
          },
          {
            key: 'salesOrder.orderNumber',
            text: 'Order Number',
          },
          {
            key: 'deliveryMethod.carrier.name',
            text: 'Carrier',
          },
          {
            key: 'consignmentReference',
            text: 'Consignment Reference',
          },
        ],
      },
      {
        value: ReportType.DispatchBoxLabel,
        label: 'Dispatch Box Labels',
      },
      {
        value: ReportType.ReceiptLabel,
        label: 'Receipt Label',
      },
      {
        value: ReportType.ProductLabel,
        label: 'Product Label (Sales Order)',
      },
      {
        value: ReportType.ProductLabelBasic,
        label: 'Product Label',
      },
      {
        value: ReportType.DayBookReport,
        label: 'Day Book Report',
      },
      {
        value: ReportType.PaymentReceipt,
        label: 'Payment Receipt',
      },
      {
        value: ReportType.NominalTrialBalance,
        label: 'Nominal Trial Balance',
      },
      {
        value: ReportType.NominalProfitLoss,
        label: 'Nominal Profit And Loss',
      },
      {
        value: ReportType.NominalProfitLossBudget,
        label: 'Nominal Profit And Loss With Budgets',
      },
      {
        value: ReportType.NominalProfitLossComparison,
        label: 'Nominal Profit And Loss Comparison',
      },
      {
        value: ReportType.NominalBalanceSheet,
        label: 'Nominal Balance Sheet',
      },
      {
        value: ReportType.NominalBalanceSheetComparison,
        label: 'Nominal Balance Sheet Comparison',
      },
      {
        value: ReportType.NominalBalanceSheetBudget,
        label: 'Nominal Balance Sheet With Budgets',
      },
      {
        value: ReportType.VatReturnReport,
        label: 'Vat Return Report',
      },
      {
        value: ReportType.VatExceptionReport,
        label: 'Vat Exception Report',
      },
      {
        value: ReportType.SalesCredit,
        label: 'Sales Credit',
      },
      {
        value: ReportType.SalesReturnNote,
        label: 'Sales Return Note',
      },
      {
        value: ReportType.NominalTransactionReport,
        label: 'Nominal Transaction',
      },
      {
        value: ReportType.TradeCounterNoSale,
        label: 'Trade Counter No Sale',
      },
      {
        value: ReportType.TradeCounterReconcile,
        label: 'Trade Counter Reconcile',
      },
      {
        value: ReportType.SubReport,
        label: 'Sub Report',
      },
      {
        value: ReportType.CustomerOverdueAccount,
        label: 'Overdue Account',
      },
      {
        value: ReportType.AssemblyPickingList,
        label: 'Assembly Picking Instruction',
      },
      {
        value: ReportType.StockAdjustmentLabel,
        label: 'Receipt Label (Stock Adjustment)',
      },
      {
        value: ReportType.DirectDebitLetter,
        label: 'Direct Debit Letter',
      },
      {
        value: ReportType.SalesOrderLabel,
        label: 'Sales Order Label',
      },
      {
        value: ReportType.ConsolidatedPick,
        label: 'Consolidated Pick',
      },
    ],
  },
  ReportTemplateFormat: {
    enumValues: [
      {
        value: ReportTemplateFormat.Jasper,
        label: 'Jasper',
      },
      {
        value: ReportTemplateFormat.Handlebars,
        label: 'HTML',
      },
    ],
  },
  QuantityToPick: {
    enumValues: [
      {
        value: QuantityToPick.CurrentAllocation,
        label: 'Current Allocation',
      },
      {
        value: QuantityToPick.NewAllocations,
        label: 'New Allocations',
      },
    ],
  },
  SalesMarginType: {
    enumValues: [
      {
        value: SalesMarginType.Sale,
        label: 'Sale',
      },
      {
        value: SalesMarginType.Cost,
        label: 'Cost',
      },
    ],
  },
  SearchBehaviour: {
    enumValues: [
      {
        value: SearchBehaviour.NextRow,
        label: 'Next Row',
      },
      {
        value: SearchBehaviour.EditQuantity,
        label: 'Edit Quantity',
      },
    ],
  },
  ShortagePromptBehaviour: {
    enumValues: [
      {
        value: SalesOrderPromptBehaviour.Disabled,
        label: 'Disabled',
      },
      {
        value: SalesOrderPromptBehaviour.PromptOnSave,
        label: 'On Save Only',
      },
      {
        value: SalesOrderPromptBehaviour.PromptOnSaveAndLineEntry,
        label: 'On Save and Line Quantity Update',
      },
    ],
  },
  BatchAllocationPromptBehaviour: {
    enumValues: [
      {
        value: SalesOrderPromptBehaviour.Disabled,
        label: 'Disabled',
      },
      {
        value: SalesOrderPromptBehaviour.PromptOnSave,
        label: 'On Save Only',
      },
      {
        value: SalesOrderPromptBehaviour.PromptOnSaveAndLineEntry,
        label: 'On Save and Line Update',
      },
    ],
  },
  StatementTransactions: {
    enumValues: [
      {
        value: StatementTransactions.All,
        label: 'All',
      },
      {
        value: StatementTransactions.Outstanding,
        label: 'Outstanding',
      },
      {
        value: StatementTransactions.Combined,
        label: 'Combined',
      },
    ],
  },
  StockAdjustmentForInsufficientStock: {
    enumValues: [
      {
        value: StockAdjustmentForInsufficientStock.Automatic,
        label: 'Enabled',
      },
      {
        value: StockAdjustmentForInsufficientStock.Disabled,
        label: 'Disabled',
      },
    ],
  },
  StockTakeMovementType: {
    enumValues: [
      {
        value: StockTakeMovementType.Single,
        label: 'Single',
      },
      {
        value: StockTakeMovementType.Multiple,
        label: 'Multiple',
      },
    ],
  },
  StockTakeType: {
    enumValues: [
      {
        value: StockTakeType.Direct,
        label: 'Direct',
      },
      {
        value: StockTakeType.Progressive,
        label: 'Progressive',
      },
    ],
  },
  StockTransactionReason: {
    enumValues: [
      {
        value: StockTransactionReason.PoReceiptAdjust,
        label: 'PO Receipt Adjustment',
      },
      {
        value: StockTransactionReason.PoReceiptCancel,
        label: 'PO Receipt Cancel',
      },
      {
        value: StockTransactionReason.CancelDispatch,
        label: 'Cancel Dispatch',
      },
      {
        value: StockTransactionReason.Dispatch,
        label: 'Dispatch',
      },
      {
        value: StockTransactionReason.GoodsReceipt,
        label: 'Goods Receipt',
      },
      {
        value: StockTransactionReason.SalesInvoice,
        label: 'Sales Invoice',
      },
      {
        value: StockTransactionReason.StockAdjustment,
        label: 'Stock Adjustment',
      },
      {
        value: StockTransactionReason.DispatchStockAdjustment,
        label: 'Dispatch Stock Adjustment',
      },
      {
        value: StockTransactionReason.StockImport,
        label: 'Stock Import',
      },
      {
        value: StockTransactionReason.StockTake,
        label: 'Stock Take',
      },
      {
        value: StockTransactionReason.StockTransfer,
        label: 'Stock Transfer',
      },
      {
        value: StockTransactionReason.CostAdjustment,
        label: 'Cost Adjustment',
      },
    ],
  },
  Supplier: {
    createDateTime: {
      hint: 'The date this account was created',
    },
  },
  SupplierAlertForm: {
    enumValues: [
      { value: SupplierAlertForm.PurchaseOrder, label: 'Purchase Order' },
      { value: SupplierAlertForm.PurchaseOrderReceipt, label: 'Purchase Order Receipt' },
      { value: SupplierAlertForm.SupplierPayment, label: 'Supplier Payment' },
      { value: SupplierAlertForm.PurchaseInvoice, label: 'Purchase Invoice' },
      { value: SupplierAlertForm.PurchaseCredit, label: 'Purchase Credit' },
      { value: SupplierAlertForm.SupplierReturn, label: 'Supplier Return' },
    ],
  },
  TemplateType: {
    enumValues: [
      {
        value: TemplateType.Account,
        label: 'Account',
      },
      {
        value: TemplateType.Value,
        label: 'Account & Value',
      },
      {
        value: TemplateType.Percentage,
        label: 'Account & Percentage',
      },
    ],
  },
  UnitOfMeasureFactorDirection: {
    enumValues: [
      {
        value: UnitOfMeasureFactorDirection.FromBaseUnit,
        label: 'From Base Unit',
      },
      {
        value: UnitOfMeasureFactorDirection.ToBaseUnit,
        label: 'To Base Unit',
      },
    ],
  },
  ValuationMethod: {
    enumValues: [
      {
        value: ValuationMethod.Manual,
        label: 'Manual',
      },
      {
        value: ValuationMethod.Replacement,
        label: 'Replacement',
      },
      {
        value: ValuationMethod.Average,
        label: 'Average',
        filter: {
          leveled: true,
        },
      },
      {
        value: ValuationMethod.Fifo,
        label: 'FIFO',
        filter: {
          leveled: true,
        },
      },
      {
        value: ValuationMethod.Batch,
        label: 'Batch',
        filter: {
          leveled: true,
        },
      },
    ],
  },
  VarianceApproval: {
    enumValues: [
      {
        value: VarianceApproval.Blank,
        label: 'Not Set',
      },
      {
        value: VarianceApproval.Preview,
        label: 'In Preview',
      },
      {
        value: VarianceApproval.Approved,
        label: 'Approved',
      },
      { value: VarianceApproval.Outstanding, label: 'Outstanding' },
    ],
  },
  VatAuthority: {
    adjustPpd: {
      hint: 'Adjust VAT amounts to handle prompt payment discounts',
    },
    cashAccountingScheme: {
      hint: 'Use the VAT Cash Accounting Scheme when calculating VAT liability',
    },
    unRealSale: {
      hint: 'Only applicable when using the cash accounting scheme',
    },
    unRealPurchase: {
      hint: 'Only applicable when using the cash accounting scheme',
    },
  },
  VatRate: {
    ecService: {
      hint: 'Is this rule for services supplied to EC countries',
    },
    vatBusinessCategory: {
      title: 'Business VAT Status',
    },
    vatProductCategory: {
      title: 'Product Vat Category',
    },
  },
  Warehouse: {
    customer: {
      hint: 'Customer where consignment stock is held',
    },
    supplier: {
      hint: 'Supplier holding supplier held stock',
    },
  },
  WarehouseType: {
    enumValues: [
      {
        value: WarehouseType.Bulk,
        label: 'Bulk',
      },
      {
        value: WarehouseType.Consignment,
        label: 'Consignment',
      },
      {
        value: WarehouseType.InTransit,
        label: 'In Transit',
      },
      {
        value: WarehouseType.Pick,
        label: 'Pick',
      },
      {
        value: WarehouseType.Quarantine,
        label: 'Quarantine',
      },
      {
        value: WarehouseType.SupplierHeld,
        label: 'Supplier Held',
      },
    ],
  },
  WindsorFrameworkAtRiskStatus: {
    enumValues: [
      {
        value: WindsorFrameworkAtRiskStatus.NotSet,
        label: 'Not Set',
      },
      {
        value: WindsorFrameworkAtRiskStatus.AtRisk,
        label: 'At Risk',
      },
      {
        value: WindsorFrameworkAtRiskStatus.NotAtRisk,
        label: 'Not At Risk',
      },
    ],
  },
  YesNoPrompt: {
    enumValues: [
      {
        value: YesNoPrompt.Yes,
        label: 'Yes',
      },
      {
        value: YesNoPrompt.No,
        label: 'No',
      },
      {
        value: YesNoPrompt.Prompt,
        label: 'Prompt User',
      },
    ],
  },
  Price: {
    enumValues: [
      {
        value: Price.ValuationCost,
        label: 'Valuation Cost',
      },
      {
        value: Price.LatestCost,
        label: 'Latest Cost',
      },
      {
        value: Price.HaggleCost,
        label: 'Haggle Cost',
      },
      {
        value: Price.SalesPriceMinimum,
        label: 'Sales Price Minimum',
      },
      {
        value: Price.SalesPrice_1,
        label: 'Sales Price 1',
      },
      {
        value: Price.SalesPrice_2,
        label: 'Sales Price 2',
      },
      {
        value: Price.SalesPrice_3,
        label: 'Sales Price 3',
      },
      {
        value: Price.SalesPrice_4,
        label: 'Sales Price 4',
      },
      {
        value: Price.SalesPrice_5,
        label: 'Sales Price 5',
      },
      {
        value: Price.SalesPrice_6,
        label: 'Sales Price 6',
      },
      {
        value: Price.SalesPrice_7,
        label: 'Sales Price 7',
      },
      {
        value: Price.SalesPrice_8,
        label: 'Sales Price 8',
      },
      {
        value: Price.SalesPrice_9,
        label: 'Sales Price 9',
      },
      {
        value: Price.SalesPrice_10,
        label: 'Sales Price 10',
      },
    ],
  },
  DeliveryNotePrintDuring: {
    enumValues: [
      {
        value: 'MANUAL',
        label: 'Manual',
      },
      {
        value: 'DISPATCH',
        label: 'On Dispatch',
      },
    ],
  },
  DiscountType: {
    enumValues: [
      {
        value: DiscountType.Percentage,
        label: 'Percent',
      },
      {
        value: DiscountType.Value,
        label: 'Value',
      },
    ],
  },
  SalesDocumentType: {
    enumValues: [
      {
        value: DocumentType.SalesInvoice,
        label: 'Invoice',
      },
      {
        value: DocumentType.SalesReceipt,
        label: 'Receipt',
      },
      {
        value: DocumentType.SalesRefund,
        label: 'Refund',
      },
      {
        value: DocumentType.SalesCredit,
        label: 'Credit',
      },
      {
        value: DocumentType.SalesOpeningBalanceInvoice,
        label: 'O/Balance Invoice',
      },
      {
        value: DocumentType.SalesOpeningBalanceCredit,
        label: 'O/Balance Credit',
      },
      {
        value: DocumentType.SalesBadDebt,
        label: 'Bad Debt Write Off',
      },
      {
        value: DocumentType.SalesVatInvoice,
        label: 'VAT Invoice',
      },
      {
        value: DocumentType.SalesVatCredit,
        label: 'VAT Credit',
      },
      {
        value: DocumentType.SalesLedgerJournal,
        label: 'Sales Journal',
      },
    ],
  },
  CostViewPermission: {
    enumValues: [
      {
        value: CostViewPermission.All,
        label: 'All costs',
      },
      {
        value: CostViewPermission.Haggle,
        label: 'Haggle Costs Only',
      },
      {
        value: CostViewPermission.None,
        label: 'No costs visible',
      },
    ],
  },
  PriceMatrixOrigin: {
    enumValues: [
      {
        value: PriceMatrixOrigin.Manual,
        label: 'Manual',
      },
      {
        value: PriceMatrixOrigin.Import,
        label: 'Import',
      },
      {
        value: PriceMatrixOrigin.NotificationAction,
        label: 'Notification Action Create',
      },
      {
        value: PriceMatrixOrigin.NotificationActionUpdate,
        label: 'Notification Action Update',
      },
    ],
  },
  PriceSelectionPermission: {
    enumValues: [
      {
        value: PriceSelectionPermission.None,
        label: 'Cannot change the selling price',
      },
      {
        value: PriceSelectionPermission.Standard,
        label: 'Can select from list',
      },
      {
        value: PriceSelectionPermission.RestrictedWithManual,
        label: 'Can select from restricted list with approved prices',
      },
      {
        value: PriceSelectionPermission.Restricted,
        label: 'Can select from restricted list',
      },
      {
        value: PriceSelectionPermission.Manual,
        label: 'Can manually change prices',
      },
    ],
  },
  CustomerStrategy: {
    enumValues: [
      {
        value: CustomerStrategy.New,
        label: 'Create new account',
      },
      {
        value: CustomerStrategy.Match,
        label: 'Attempt to match',
      },
      {
        value: CustomerStrategy.One,
        label: 'Use a named account',
      },
    ],
  },
  Site: {
    enumValues: Object.entries(Site).map(([key, value]) => {
      return {
        value: value,
        label: key,
      };
    }),
  },
  CarrierIntegrationType: {
    enumValues: Object.entries(CarrierIntegrationType).map(([key, value]) => {
      return {
        value: value,
        label: key,
      };
    }),
  },
  LabelPrintOption: {
    enumValues: [
      {
        value: LabelPrintOption.Automatic,
        label: 'Automatic',
      },
      {
        value: LabelPrintOption.Prompt,
        label: 'Prompt',
      },
      {
        value: LabelPrintOption.Manual,
        label: 'Manual',
      },
    ],
  },
  PrinterType: {
    enumValues: [
      {
        value: PrinterType.System,
        label: 'System',
      },
      {
        value: PrinterType.Local,
        label: 'Local',
      },
    ],
  },
  LabelFormat: {
    enumValues: [
      {
        value: LabelFormat.Pdf,
        label: 'PDF',
      },
      {
        value: LabelFormat.Zpl,
        label: 'Zebra Print',
      },
      {
        value: LabelFormat.Png,
        label: 'PNG',
      },
    ],
  },
  VatType: {
    enumValues: [
      {
        value: VatType.Input,
        label: 'Input',
      },
      {
        value: VatType.Output,
        label: 'Output',
      },
    ],
  },
  VatObligationStatus: {
    enumValues: [
      {
        value: VatObligationStatus.Fulfilled,
        label: 'Fulfilled',
      },
      {
        value: VatObligationStatus.Open,
        label: 'Open',
      },
    ],
  },
  MassUom: {
    enumValues: [
      {
        value: MassUom.Ounce,
        label: 'Ounce (Oz)',
      },
      {
        value: MassUom.Gram,
        label: 'Gram (g)',
      },
      {
        value: MassUom.Kilogram,
        label: 'Kilogram (kg)',
      },
    ],
  },
  SalesOrderLineStatus: {
    enumValues: [
      {
        value: SalesOrderLineStatus.PriceApprovalRequired,
        label: 'Price Approval Required',
      },
      {
        value: SalesOrderLineStatus.Quoted,
        label: 'Quoted',
      },
      {
        value: SalesOrderLineStatus.Enquiring,
        label: 'Enquiring',
      },
      {
        value: SalesOrderLineStatus.AssignedAssemblyShortage,
        label: 'Assembly To Be Raised',
      },
      {
        value: SalesOrderLineStatus.AssignedAssembly,
        label: 'Awaiting Assembly',
      },
      {
        value: SalesOrderLineStatus.AssignedShortage,
        label: 'Awaiting Purchase',
      },
      {
        value: SalesOrderLineStatus.Assigned,
        label: 'Assigned',
      },
      {
        value: SalesOrderLineStatus.DirectDelivery,
        label: 'Direct Delivery',
      },
      {
        value: SalesOrderLineStatus.AwaitingProduction,
        label: 'Awaiting Production',
      },
      {
        value: SalesOrderLineStatus.AwaitingAllocation,
        label: 'Awaiting Allocation',
      },
      {
        value: SalesOrderLineStatus.Reserved,
        label: 'Reserved',
      },
      {
        value: SalesOrderLineStatus.InProduction,
        label: 'In Production',
      },
      {
        value: SalesOrderLineStatus.Allocated,
        label: 'Allocated',
      },
      {
        value: SalesOrderLineStatus.FullyAllocated,
        label: 'Fully Allocated',
      },
      {
        value: SalesOrderLineStatus.AwaitingPick,
        label: 'Awaiting Pick',
      },
      {
        value: SalesOrderLineStatus.Picked,
        label: 'Picked',
      },
      {
        value: SalesOrderLineStatus.Dispatched,
        label: 'Dispatched',
      },
      {
        value: SalesOrderLineStatus.OutForDelivery,
        label: 'Out for Delivery',
      },
      {
        value: SalesOrderLineStatus.Delivered,
        label: 'Delivered',
      },
      {
        value: SalesOrderLineStatus.Invoiced,
        label: 'Invoiced',
      },
      {
        value: SalesOrderLineStatus.Returned,
        label: 'Returned',
      },
      {
        value: SalesOrderLineStatus.Reserved,
        label: 'Reserved',
      },
      {
        value: SalesOrderLineStatus.Pending,
        label: 'Pending',
      },
      {
        value: SalesOrderLineStatus.Cancelled,
        label: 'Cancelled',
      },
    ],
  },
  SupplierReturnStatus: {
    enumValues: [
      {
        value: SupplierReturnStatus.AwaitingReturn,
        label: 'Awaiting Return',
      },
      {
        value: SupplierReturnStatus.Returned,
        label: 'Returned',
      },
      {
        value: SupplierReturnStatus.DebitNote,
        label: 'Debit Note',
      },
      {
        value: SupplierReturnStatus.Credited,
        label: 'Credited',
      },
    ],
  },
  AlternativeProductType: {
    enumValues: [
      {
        value: AlternativeProductType.Related,
        label: 'Related',
      },
      {
        value: AlternativeProductType.Supersession,
        label: 'Supersession',
      },
      {
        value: AlternativeProductType.Crosssell,
        label: 'Cross-sell',
      },
      {
        value: AlternativeProductType.Upsell,
        label: 'Upsell',
      },
    ],
  },
  GenericChannelType: {
    enumValues: [
      {
        value: GenericChannelType.Website,
        label: 'Website',
      },
      {
        value: GenericChannelType.Other,
        label: 'Other',
      },
    ],
  },
  WidgetWorkspace: {
    enumValues: [
      {
        value: WidgetWorkspace.Customers,
        label: 'Customer Workspace',
      },
      {
        value: WidgetWorkspace.Suppliers,
        label: 'Supplier Workspace',
      },
      {
        value: WidgetWorkspace.SalesInvoices,
        label: 'Invoices Workspace',
      },
      {
        value: WidgetWorkspace.SalesInvoiceLines,
        label: 'Invoice Lines Workspace',
      },
      {
        value: WidgetWorkspace.SalesReceipts,
        label: 'Sales Receipts Workspace',
      },
      {
        value: WidgetWorkspace.NominalAccounts,
        label: 'Nominal Accounts Workspace',
      },
      {
        value: WidgetWorkspace.NominalTransactions,
        label: 'Nominal Transactions Workspace',
      },
      {
        value: WidgetWorkspace.BankAccounts,
        label: 'Bank Accounts Workspace',
      },
      {
        value: WidgetWorkspace.Products,
        label: 'Products Workspace',
      },
      {
        value: WidgetWorkspace.SalesOrders,
        label: 'Sales Order Workspace',
      },
      {
        value: WidgetWorkspace.SalesQuotes,
        label: 'Quotes Workspace',
      },
      {
        value: WidgetWorkspace.SalesOrdersAndQuotes,
        label: 'Orders & Quotes Workspace',
      },
      {
        value: WidgetWorkspace.SalesOrderItems,
        label: 'Sales Order Items Workspace',
      },
      {
        value: WidgetWorkspace.Tasks,
        label: 'Tasks Workspace',
      },
      {
        value: WidgetWorkspace.PurchaseOrders,
        label: 'Purchase Order Workspace',
      },
      {
        value: WidgetWorkspace.SalesCredits,
        label: 'Sales Credits',
      },
      {
        value: WidgetWorkspace.SalesCreditLines,
        label: 'Sales Credit Lines',
      },
      {
        value: WidgetWorkspace.CustomerRefunds,
        label: 'Customer Refunds',
      },
      {
        value: WidgetWorkspace.CustomerBadDebts,
        label: 'Customer Bad Debt',
      },
      {
        value: WidgetWorkspace.SalesTransactions,
        label: 'Sales Transactions',
      },
      {
        value: WidgetWorkspace.ImportedAmazonOrders,
        label: 'Imported Amazon Orders',
      },
      {
        value: WidgetWorkspace.ImportedEbayOrders,
        label: 'Imported Ebay Orders',
      },
      {
        value: WidgetWorkspace.ImportedShopifyOrders,
        label: 'Imported Shopify Orders',
      },
      {
        value: WidgetWorkspace.ImportedPixSellOrders,
        label: 'Imported PixSell Orders',
      },
      {
        value: WidgetWorkspace.ImportedWooCommerceOrders,
        label: 'Imported WooCommerce Orders',
      },
      {
        value: WidgetWorkspace.NominalAccrualsAndPrepayments,
        label: 'Nominal Accruals & Prepayments',
      },
      {
        value: WidgetWorkspace.VatTransactions,
        label: 'VAT Transactions',
      },
      {
        value: WidgetWorkspace.PurchaseTransactions,
        label: 'Purchase Transactions',
      },
      {
        value: WidgetWorkspace.PurchaseInvoices,
        label: 'Purchase Invoices',
      },
    ],
  },
  ColourScheme: {
    enumValues: [
      {
        value: 'OGL',
        label: 'OGL',
      },
      {
        value: 'STANDARD',
        label: 'Alternate',
      },
      {
        value: 'RAINBOW',
        label: 'Rainbow',
      },
      {
        value: 'BLIND',
        label: 'Colour Blind',
      },
    ],
    enumValuesLimited: [
      {
        value: 'STANDARD',
        label: 'Standard',
      },
      {
        value: 'BLIND',
        label: 'Colour Blind',
      },
    ],
  },
  Operation: {
    enumValues: [
      {
        value: Operation.Sum,
        label: 'sum',
      },
      {
        value: Operation.Count,
        label: 'count',
      },
      {
        value: Operation.Average,
        label: 'average',
      },
    ],
  },
  FeedRuleStringCondition: {
    enumValues: [
      {
        value: FeedRuleStringCondition.Exact,
        label: 'Exact',
      },
      {
        value: FeedRuleStringCondition.Includes,
        label: 'Includes',
      },
      {
        value: FeedRuleStringCondition.Startswith,
        label: 'Starts With',
      },
      {
        value: FeedRuleStringCondition.Endswith,
        label: 'Ends With',
      },
    ],
  },
  FeedRuleAmountCondition: {
    enumValues: [
      {
        value: FeedRuleAmountCondition.Exact,
        label: 'Exact',
      },
      {
        value: FeedRuleAmountCondition.Greaterthan,
        label: 'Greater Than',
      },
      {
        value: FeedRuleAmountCondition.Lessthan,
        label: 'Less Than',
      },
      {
        value: FeedRuleAmountCondition.Between,
        label: 'Between',
      },
    ],
  },
  PurchaseDocumentType: {
    enumValues: [
      {
        value: DocumentType.PurchaseInvoice,
        label: 'Invoice',
      },
      {
        value: DocumentType.PurchasePayment,
        label: 'Payment',
      },
      {
        value: DocumentType.PurchaseCredit,
        label: 'Credit',
      },
      {
        value: DocumentType.PurchaseOpeningBalanceInvoice,
        label: 'O/Balance Invoice',
      },
      {
        value: DocumentType.PurchaseOpeningBalanceCredit,
        label: 'O/Balance Credit',
      },
      {
        value: DocumentType.PurchaseVatInvoice,
        label: 'VAT Invoice',
      },
      {
        value: DocumentType.PurchaseVatCredit,
        label: 'VAT Credit',
      },
      {
        value: DocumentType.PurchaseRefund,
        label: 'Refund',
      },
      {
        value: DocumentType.PurchaseLedgerJournal,
        label: 'Purchase Journal',
      },
    ],
  },
  VatOnlyTransactionType: {
    enumValues: [
      {
        value: VatOnlyTransactionType.Invoice,
        label: 'Vat Only Invoice',
      },
      {
        value: VatOnlyTransactionType.Credit,
        label: 'Vat Only Credit',
      },
    ],
  },
  SalesOrderActivationType: {
    enumValues: [
      {
        value: SalesOrderActivationType.Forward,
        label: 'Forward',
      },
      {
        value: SalesOrderActivationType.Scheduled,
        label: 'Scheduled',
      },
    ],
  },
  SupplierPaymentType: {
    enumValues: [
      {
        value: SupplierPaymentType.Cash,
        label: 'Cash',
      },
      {
        value: SupplierPaymentType.Cheque,
        label: 'Cheque',
      },
      {
        value: SupplierPaymentType.CreditCard,
        label: 'Credit Card',
      },
      {
        value: SupplierPaymentType.Bacs,
        label: 'BACS',
      },
      {
        value: SupplierPaymentType.DirectDebit,
        label: 'Direct Debit',
      },
      {
        value: SupplierPaymentType.StandingOrder,
        label: 'Standing Order',
      },
      {
        value: SupplierPaymentType.Other,
        label: 'Other',
      },
    ],
  },
  SupplierPaymentPriority: {
    enumValues: [
      {
        value: SupplierPaymentPriority.A,
        label: 'High',
      },
      {
        value: SupplierPaymentPriority.B,
        label: 'Normal',
      },
      {
        value: SupplierPaymentPriority.C,
        label: 'Low',
      },
      {
        value: SupplierPaymentPriority.Normal,
        label: 'Not-Set',
      },
    ],
  },
  RestockOption: {
    enumValues: [
      {
        value: null,
        label: 'Sales Requirements Only',
      },
      {
        value: RestockOption.Minimum,
        label: 'Restock to Minimum',
      },
      {
        value: RestockOption.Optimum,
        label: 'Restock to Optimum',
      },
      {
        value: RestockOption.Maximum,
        label: 'Restock to Maximum',
      },
    ],
  },
  ShortageOrigin: {
    enumValues: [
      {
        value: ShortageOrigin.SalesOrder,
        label: 'Sales Order',
      },
      {
        value: ShortageOrigin.RestockMinimum,
        label: 'Restock to Minimum',
      },
      {
        value: ShortageOrigin.RestockOptimum,
        label: 'Restock to Optimum',
      },
      {
        value: ShortageOrigin.RestockMaximum,
        label: 'Restock to Maximum',
      },
      {
        value: ShortageOrigin.RestockManual,
        label: 'Restock Manually',
      },
    ],
  },
  DatabaseDriver: {
    enumValues: [
      {
        value: DatabaseDriver.Postgresql,
        label: 'PostgreSQL',
      },
    ],
  },
  FilterDateType: {
    enumValues: [
      {
        value: FilterDateType.DateRange,
        label: 'Date Range',
      },
      {
        value: FilterDateType.Today,
        label: 'Today',
      },
      {
        value: FilterDateType.Tomorrow,
        label: 'Tomorrow',
      },
      {
        value: FilterDateType.Yesterday,
        label: 'Yesterday',
      },
      {
        value: FilterDateType.NextXDays,
        label: 'Next X Days',
      },
      {
        value: FilterDateType.LastXDays,
        label: 'Last X Days',
      },
      {
        value: FilterDateType.ThisWeek,
        label: 'This Week',
      },
      {
        value: FilterDateType.NextWeek,
        label: 'Next Week',
      },
      {
        value: FilterDateType.LastWeek,
        label: 'Last Week',
      },
      {
        value: FilterDateType.NextXWeeks,
        label: 'Next X Weeks',
      },
      {
        value: FilterDateType.LastXWeeks,
        label: 'Last X Weeks',
      },
      {
        value: FilterDateType.ThisMonth,
        label: 'This Month',
      },
      {
        value: FilterDateType.NextMonth,
        label: 'Next Month',
      },
      {
        value: FilterDateType.LastMonth,
        label: 'Last Month',
      },
      {
        value: FilterDateType.NextXMonths,
        label: 'Next X Months',
      },
      {
        value: FilterDateType.LastXMonths,
        label: 'Last X Months',
      },
      {
        value: FilterDateType.ThisQuarter,
        label: 'This Quarter',
      },
      {
        value: FilterDateType.NextQuarter,
        label: 'Next Quarter',
      },
      {
        value: FilterDateType.LastQuarter,
        label: 'Last Quarter',
      },
      {
        value: FilterDateType.NextXQuarters,
        label: 'Next X Quarters',
      },
      {
        value: FilterDateType.LastXQuarters,
        label: 'Last X Quarters',
      },
      {
        value: FilterDateType.ThisYear,
        label: 'This Year',
      },
      {
        value: FilterDateType.NextYear,
        label: 'Next Year',
      },
      {
        value: FilterDateType.LastYear,
        label: 'Last Year',
      },
      {
        value: FilterDateType.NextXYears,
        label: 'Next X Years',
      },
      {
        value: FilterDateType.LastXYears,
        label: 'Last X Years',
      },
    ],
  },
  FilterDateRelative: {
    enumValues: [
      {
        value: FilterDateRelative.Today,
        label: 'Today',
      },
      {
        value: FilterDateRelative.StartOfWeek,
        label: 'Start of Week',
      },
      {
        value: FilterDateRelative.EndOfWeek,
        label: 'End of Week',
      },
      {
        value: FilterDateRelative.StartOfMonth,
        label: 'Start of Month',
      },
      {
        value: FilterDateRelative.EndOfMonth,
        label: 'End of Month',
      },
      {
        value: FilterDateRelative.StartOfQuarter,
        label: 'Start of Quarter',
      },
      {
        value: FilterDateRelative.EndOfQuarter,
        label: 'End of Quarter',
      },
      {
        value: FilterDateRelative.StartOfYear,
        label: 'Start of Year',
      },
      {
        value: FilterDateRelative.EndOfYear,
        label: 'End of Year',
      },
    ],
  },
  FilterNumberType: {
    enumValues: [
      {
        value: FilterNumberType.Range,
        label: 'Between',
      },
      {
        value: FilterNumberType.Equals,
        label: 'Equals',
      },
      {
        value: FilterNumberType.NotEquals,
        label: 'Does Not Equal',
      },
      {
        value: FilterNumberType.LessThan,
        label: 'Less Than',
      },
      {
        value: FilterNumberType.LessThanEqual,
        label: 'Less Than or Equal To',
      },
      {
        value: FilterNumberType.GreaterThan,
        label: 'Greater Than',
      },
      {
        value: FilterNumberType.GreaterThanEqual,
        label: 'Greater Than or Equal To',
      },
      {
        value: FilterNumberType.In,
        label: 'Any Of',
      },
    ],
  },
  FilterStringType: {
    enumValues: [
      {
        value: FilterStringType.Contains,
        label: 'Contains',
      },
      {
        value: FilterStringType.Equals,
        label: 'Equals',
      },
      {
        value: FilterStringType.NotEquals,
        label: 'Does Not Equal',
      },
      {
        value: FilterStringType.StartsWith,
        label: 'Starts With',
      },
      {
        value: FilterStringType.EndsWith,
        label: 'Ends With',
      },
      {
        value: FilterStringType.In,
        label: 'Any Of',
      },
    ],
  },
  CostForMargin: {
    enumValues: [
      {
        value: Price.LatestCost,
        label: 'Latest Cost',
      },
      {
        value: Price.ValuationCost,
        label: 'Valuation Cost',
      },
    ],
  },
  HeldAction: {
    enumValues: [
      {
        value: HeldAction.Warn,
        label: 'Warning',
      },
      {
        value: HeldAction.Prevent,
        label: 'Prevent',
      },
    ],
  },
  ReportLayoutAlignment: {
    enumValues: [
      {
        value: ReportLayoutAlignment.Right,
        label: 'Right',
      },
      {
        value: ReportLayoutAlignment.Left,
        label: 'Left',
      },
    ],
  },
  ReportLayoutStyle: {
    enumValues: [
      {
        value: ReportLayoutStyle.Text,
        label: 'Normal',
      },
      {
        value: ReportLayoutStyle.HeaderOne,
        label: 'Header 1',
      },
      {
        value: ReportLayoutStyle.HeaderTwo,
        label: 'Header 2',
      },
      {
        value: ReportLayoutStyle.HeaderThree,
        label: 'Header 3',
      },
      {
        value: ReportLayoutStyle.TotalOne,
        label: 'Total 1',
      },
      {
        value: ReportLayoutStyle.TotalTwo,
        label: 'Total 2',
      },
      {
        value: ReportLayoutStyle.TotalThree,
        label: 'Total 3',
      },
    ],
  },
  ReportLayoutLineType: {
    enumValues: [
      {
        value: ReportLayoutLineType.FreeText,
        label: 'Free Text',
      },
      {
        value: ReportLayoutLineType.AccountGroup,
        label: 'Account Group',
      },
      {
        value: ReportLayoutLineType.Subtotal,
        label: 'Subtotal',
      },
      {
        value: ReportLayoutLineType.Space,
        label: 'Spacing',
      },
      {
        value: ReportLayoutLineType.ProfitAndLoss,
        label: 'Profit and Loss',
      },
    ],
  },
  ReportLayoutCredit: {
    enumValues: [
      {
        value: ReportLayoutCredit.Credit,
        label: 'Credit',
      },
      {
        value: ReportLayoutCredit.Debit,
        label: 'Debit',
      },
    ],
  },
  ReceiptLabelType: {
    enumValues: [
      {
        value: ReceiptLabelType.Line,
        label: 'One per Order Line',
      },
      {
        value: ReceiptLabelType.Piece,
        label: 'One per Unit Quantity',
      },
      {
        value: ReceiptLabelType.Lot,
        label: 'One per Batch / Traceable Lot ',
      },
      {
        value: ReceiptLabelType.Order,
        label: 'One per Order',
      },
    ],
  },
  AvailabilityCalculationBasis: {
    enumValues: [
      {
        value: AvailabilityCalculationBasis.UsageTotal,
        label: 'Usage Total',
      },
      {
        value: AvailabilityCalculationBasis.SalesOnly,
        label: 'Sales Only',
      },
      {
        value: AvailabilityCalculationBasis.PurchasesOnly,
        label: 'Purchases Only',
      },
    ],
  },
  TermsOfDelivery: {
    enumValues: [
      {
        value: TermsOfDelivery.Exw,
        label: 'EXW - Ex Works',
      },
      {
        value: TermsOfDelivery.Fca,
        label: 'FCA - Free carrier',
      },
      {
        value: TermsOfDelivery.Fas,
        label: 'FAS - Free alongside ship',
      },
      {
        value: TermsOfDelivery.Fob,
        label: 'FOB - Free on board',
      },
      {
        value: TermsOfDelivery.Cfr,
        label: 'CFR - Cost & freight C&F',
      },
      {
        value: TermsOfDelivery.Cif,
        label: 'CIF - Cost, insurance and freight',
      },
      {
        value: TermsOfDelivery.Cpt,
        label: 'CPT - Carriage paid to',
      },
      {
        value: TermsOfDelivery.Cip,
        label: 'CIP - Carriage and insurance paid to',
      },
      {
        value: TermsOfDelivery.Daf,
        label: 'DAF - Delivered at frontier',
      },
      {
        value: TermsOfDelivery.Des,
        label: 'DES - Delivery ex-ship',
      },
      {
        value: TermsOfDelivery.Deq,
        label: 'DEQ - Delivered ex-quay',
      },
      {
        value: TermsOfDelivery.Ddu,
        label: 'DDU - Delivered, duty unpaid',
      },
      {
        value: TermsOfDelivery.Ddp,
        label: 'DDP - Delivered, duty paid',
      },
      {
        value: TermsOfDelivery.Dap,
        label: 'DAP - Delivered at place',
      },
      {
        value: TermsOfDelivery.Dat,
        label: 'DAT - Delivered at terminal',
      },
      {
        value: TermsOfDelivery.Xxx,
        label: 'XXX - Other terms not listed',
      },
    ],
  },
  CostOfSaleMethod: {
    enumValues: [
      {
        value: CostOfSaleMethod.Manual,
        label: 'Manual Journal',
      },
      {
        value: CostOfSaleMethod.AutomaticUpdate,
        label: 'Generate Postings Immediately',
      },
      {
        value: CostOfSaleMethod.BatchUpdate,
        label: 'Generate Batch Postings',
      },
    ],
  },
  ImportRecordType: {
    enumValues: [
      {
        value: ImportRecordType.All,
        label: 'All records',
      },
      {
        value: ImportRecordType.New,
        label: 'New records only',
      },
      {
        value: ImportRecordType.Existing,
        label: 'Existing records only',
      },
    ],
  },
  QuoteEventLeadTime: {
    enumValues: [
      {
        value: QuoteEventLeadTime.OrderDate,
        label: 'Order Date',
      },
      {
        value: QuoteEventLeadTime.ExpectedDeliveryDate,
        label: 'Delivery Date',
      },
      {
        value: QuoteEventLeadTime.ExpiryDate,
        label: 'Expiry Date',
      },
    ],
  },
  OrderEventLeadTime: {
    enumValues: [
      {
        value: OrderEventLeadTime.OrderDate,
        label: 'Order Date',
      },
      {
        value: OrderEventLeadTime.ExpectedDeliveryDate,
        label: 'Delivery Date',
      },
    ],
  },
  QuoteStatus: {
    enumValues: [
      {
        value: QuoteStatus.Open,
        label: 'Open',
      },
      {
        value: QuoteStatus.Accepted,
        label: 'Accepted',
      },
      {
        value: QuoteStatus.Lost,
        label: 'Lost',
      },
    ],
  },
  OrderType: {
    enumValues: [
      {
        value: OrderType.Enquiry,
        label: 'Enquiry',
      },
      {
        value: OrderType.Quote,
        label: 'Quote',
      },
      {
        value: OrderType.Order,
        label: 'Order',
      },
    ],
  },
  AccountingScheme: {
    enumValues: [
      {
        value: AccountingScheme.Standard,
        label: 'Standard',
      },
      {
        value: AccountingScheme.Cash,
        label: 'Cash',
      },
    ],
  },
  VatTransaction: {
    vatBusinessCategory: {
      title: 'Business VAT Status',
    },
    vatProductCategory: {
      title: 'Product Vat Category',
    },
  },
  PostingPermissionType: {
    enumValues: [
      {
        value: PostingPermissionType.Yes,
        label: 'Yes',
      },
      {
        value: PostingPermissionType.No,
        label: 'No',
      },
      {
        value: PostingPermissionType.Warn,
        label: 'Warning',
      },
    ],
  },
  OrderImportFailureReason: {
    enumValues: [
      {
        value: OrderImportFailureReason.SkuNotPresent,
        label: 'SKU not present',
      },
      {
        value: OrderImportFailureReason.ProductNotFound,
        label: 'Product not found',
      },
      {
        value: OrderImportFailureReason.Unknown,
        label: 'Unknown',
      },
    ],
  },
  OrderOrigin: {
    enumValues: [
      {
        value: OrderOrigin.Manual,
        label: 'Manual',
      },
      {
        value: OrderOrigin.Import,
        label: 'Import',
      },
      {
        value: OrderOrigin.Amazon,
        label: 'Amazon',
      },
      {
        value: OrderOrigin.AmazonPrime,
        label: 'Amazon Prime',
      },
      {
        value: OrderOrigin.AmazonFba,
        label: 'Amazon FBA',
      },
      {
        value: OrderOrigin.Ebay,
        label: 'eBay',
      },
      {
        value: OrderOrigin.Pixsell,
        label: 'PixSell',
      },
      {
        value: OrderOrigin.Web,
        label: 'Web',
      },
      {
        value: OrderOrigin.Shopify,
        label: 'Shopify',
      },
      {
        value: OrderOrigin.Woocommerce,
        label: 'WooCommerce',
      },
      {
        value: OrderOrigin.Xedi,
        label: 'Xedi',
      },
      {
        value: OrderOrigin.TradeCounter,
        label: 'Trade Counter',
      },
      {
        value: OrderOrigin.BigCommerce,
        label: 'BigCommerce',
      },
      {
        value: OrderOrigin.Magento,
        label: 'Magento',
      },
    ],
  },
  ChannelFeatureStatus: {
    enumValues: [
      {
        value: ChannelFeatureStatus.Disabled,
        label: 'Disabled',
      },
      {
        value: ChannelFeatureStatus.Auto,
        label: 'Automatic',
      },
      {
        value: ChannelFeatureStatus.Manual,
        label: 'Manual',
      },
    ],
  },
  ScanFormStatus: {
    enumValues: [
      {
        value: ScanFormStatus.Creating,
        label: 'Creating',
      },
      {
        value: ScanFormStatus.Created,
        label: 'Created',
      },
      {
        value: ScanFormStatus.Failed,
        label: 'Failed',
      },
    ],
  },
  EmailType: {
    enumValues: [
      {
        value: EmailType.InlineHtml,
        label: 'Inline',
      },
      {
        value: EmailType.Attachment,
        label: 'PDF Attachment',
      },
    ],
  },
  Title: {
    enumValues: [
      {
        value: null,
        label: 'No title',
      },
      {
        value: Title.Mr,
        label: 'Mr',
      },
      {
        value: Title.Mrs,
        label: 'Mrs',
      },
      {
        value: Title.Miss,
        label: 'Miss',
      },
      {
        value: Title.Ms,
        label: 'Ms',
      },
      {
        value: Title.Mx,
        label: 'Mx',
      },
      {
        value: Title.Dr,
        label: 'Dr',
      },
      {
        value: Title.Prof,
        label: 'Prof',
      },
    ],
  },
  FilterStyle: {
    enumValues: [
      {
        value: FilterStyle.Default,
        label: 'Default',
      },
      {
        value: FilterStyle.List,
        label: 'List Selection',
      },
    ],
  },
  InvoiceFrequency: {
    enumValues: [
      {
        value: InvoiceFrequency.Daily,
        label: 'Daily',
      },
      {
        value: InvoiceFrequency.Weekly,
        label: 'Weekly',
      },
      {
        value: InvoiceFrequency.Monthly,
        label: 'Monthly',
      },
    ],
  },
  PickType: {
    enumValues: [
      {
        value: PickType.Pick,
        label: 'Pick',
      },
      {
        value: PickType.Quarantine,
        label: 'Quarantine',
      },
    ],
  },
  ProductSpecialContractPriceOnly: {
    enumValues: [
      {
        value: ProductSpecialContractPriceOnly.Any,
        label: 'Any',
      },
      {
        value: ProductSpecialContractPriceOnly.SpecialPriceOnly,
        label: 'Special Price Only',
      },
      {
        value: ProductSpecialContractPriceOnly.ContractPriceOnly,
        label: 'Contract Price Only',
      },
    ],
  },
  PurchasePaymentExportType: {
    enumValues: [
      {
        value: PurchasePaymentExportType.Natwest,
        label: 'Natwest',
      },
      {
        value: PurchasePaymentExportType.NatwestAdHoc,
        label: 'Natwest - Ad Hoc Bulk Payment',
      },
      {
        value: PurchasePaymentExportType.NatwestBankTxt,
        label: 'Natwest Bank Txt',
      },
      {
        value: PurchasePaymentExportType.VirginMoney,
        label: 'Virgin Money',
      },
      {
        value: PurchasePaymentExportType.Barclays,
        label: 'Barclays',
      },
      {
        value: PurchasePaymentExportType.Lloyds,
        label: 'Lloyds',
      },
      {
        value: PurchasePaymentExportType.Rbs,
        label: 'RBS',
      },
      {
        value: PurchasePaymentExportType.HsbcBankTxt,
        label: 'HSBC',
      },
      {
        value: PurchasePaymentExportType.Santander,
        label: 'Santander',
      },
    ],
  },
  TransactionType: {
    enumValues: Object.entries(TransactionType).map(([key, value]) => {
      return {
        value: value,
        label: key,
      };
    }),
  },
  TransactionNotification: {
    enumValues: Object.entries(TransactionNotification).map(([key, value]) => {
      return {
        value: value,
        label: key,
      };
    }),
  },
  TransactionResult: {
    enumValues: Object.entries(TransactionResult).map(([key, value]) => {
      return {
        value: value,
        label: key,
      };
    }),
  },
  TransactionNoteType: {
    enumValues: Object.entries(TransactionNoteType).map(([key, value]) => {
      return {
        value: value,
        label: key,
      };
    }),
  },
  AdditionalInstructionSource: {
    enumValues: Object.entries(AdditionalInstructionSource).map(([key, value]) => {
      return {
        value: value,
        label: key,
      };
    }),
  },
  CapacityField: {
    enumValues: Object.entries(CapacityField).map(([key, value]) => {
      return {
        value: value,
        label: key,
      };
    }),
  },
  SmtpAuthMechanism: {
    enumValues: [
      {
        value: SmtpAuthMechanism.Login,
        label: 'Username and Password',
      },
      {
        value: SmtpAuthMechanism.Msft,
        label: 'Microsoft OAuth',
      },
    ],
  },
  StockLotSortOption: {
    enumValues: [
      {
        value: StockLotSortOption.LotFirst,
        label: 'Lot attribute values first',
      },
      {
        value: StockLotSortOption.BinSeqFirst,
        label: 'Bin location pick sequence first',
      },
    ],
  },
  AssemblyStatus: {
    enumValues: Object.entries(AssemblyStatus).map(([, value]) => {
      return {
        value: value,
        label: enumToTitleCase(value),
      };
    }),
  },
  UserActionScope: {
    enumValues: [
      {
        value: UserActionScope.Disabled,
        label: 'No',
      },
      {
        value: UserActionScope.Enabled,
        label: 'Yes',
      },
      {
        value: UserActionScope.AccountManagementGroup,
        label: 'If in users account management group',
      },
    ],
  },
  AutomatedCreditControlActionType: {
    enumValues: [
      {
        value: AutomatedCreditControlActionType.AutomaticEmail,
        label: 'Automated E-Mail',
      },
      {
        value: AutomatedCreditControlActionType.ApprovedEmail,
        label: 'Approved E-Mail',
      },
      {
        value: AutomatedCreditControlActionType.Task,
        label: 'Task',
      },
    ],
  },
  AutomatedCreditControlTargetContact: {
    enumValues: [
      {
        value: AutomatedCreditControlTargetContact.SalesInvoice,
        label: 'Sales Invoice Recipient',
      },
      {
        value: AutomatedCreditControlTargetContact.Statement,
        label: 'Statement Recipient',
      },
      {
        value: AutomatedCreditControlTargetContact.OverdueAccount,
        label: 'Overdue Account Recipient',
      },
    ],
  },
  BinReplenishmentStatus: {
    enumValues: [
      {
        value: BinReplenishmentStatus.Unapproved,
        label: 'Unapproved',
      },
      {
        value: BinReplenishmentStatus.Approved,
        label: 'Approved',
      },
      {
        value: BinReplenishmentStatus.TransferRaised,
        label: 'Transfer Raised',
      },
      {
        value: BinReplenishmentStatus.Archived,
        label: 'Archived',
      },
    ],
  },
  SalesValueComparisonType: {
    enumValues: [
      {
        value: SalesValueComparisonType.NettGoods,
        label: 'Nett Goods Total',
      },
      {
        value: SalesValueComparisonType.HeaderTotal,
        label: 'Order Header Total',
      },
    ],
  },
  DeliveryRouteTimeRestriction: {
    enumValues: [
      {
        value: DeliveryRouteTimeRestriction.DayOnly,
        label: 'Day Only',
      },
      {
        value: DeliveryRouteTimeRestriction.NightOnly,
        label: 'Night Only',
      },
      {
        value: DeliveryRouteTimeRestriction.None,
        label: 'None',
      },
    ],
  },
  SalesCreditType: {
    enumValues: [
      {
        value: SalesCreditType.Credit,
        label: 'Credit',
      },
      {
        value: SalesCreditType.Return,
        label: 'Return',
      },
      {
        value: SalesCreditType.Refund,
        label: 'Refund',
      },
    ],
  },
  TargetDateSource: {
    enumValues: [
      {
        value: TargetDateSource.None,
        label: 'None',
      },
      {
        value: TargetDateSource.LastDispatchDate,
        label: 'Last Dispatch Date',
      },
      {
        value: TargetDateSource.LastDeliveryDate,
        label: 'Last Delivery Date',
      },
    ],
  },
  ShipTheoryReferenceOption: {
    enumValues: [
      {
        value: ShipTheoryReferenceOption.DispatchDocumentReference,
        label: 'Dispatch Document Reference',
      },
      {
        value: ShipTheoryReferenceOption.CustomerOrderReference,
        label: 'Customer Order Reference',
      },
    ],
  },
  StockCommitmentType: {
    enumValues: [
      {
        value: StockCommitmentType.Allocation,
        label: 'Allocation',
      },
      {
        value: StockCommitmentType.Reservation,
        label: 'Reservation',
      },
      {
        value: StockCommitmentType.StockTransfer,
        label: 'Stock Transfer',
      },
    ],
  },
  DocumentSummaryStatus: {
    enumValues: [
      {
        value: DocumentSummaryStatus.NotRequired,
        label: 'Not Required',
      },
      {
        value: DocumentSummaryStatus.Unprocessed,
        label: 'Unprocessed',
      },
      {
        value: DocumentSummaryStatus.Processed,
        label: 'Processed',
      },
      {
        value: DocumentSummaryStatus.Interrupted,
        label: 'Interrupted',
      },
      {
        value: DocumentSummaryStatus.Indeterminate,
        label: 'Indeterminate',
      },
      {
        value: DocumentSummaryStatus.Complete,
        label: 'Complete',
      },
    ],
  },
  Marketplace: {
    enumValues: Object.entries(Marketplace).map(([, value]) => {
      return {
        value: value,
        label: value,
      };
    }),
  },
};

export default fieldMetaData;
